// Kursusindhold: Forældreansvarsloven § for § (LBK nr. 662 af 1. juli 2026)
// Scene- og spørgsmåls-id'er er stabile og bruges i databasen. Omdøb dem ikke.
// Nye spørgsmål skal have nye id'er. Hæv 'version', når loven ændres.
//
// Felter pr. scene: id, c (kapitelindeks), p (paragraf), t (titel), s (forklaring),
// pr (i praksis), nyt (mærke), v (visuel), law ([[paragraf, tekst med | mellem stykker]]),
// quiz (1 = quizscene med qs), end (1 = slutscene).

export const COURSE = {
 "id": "foraeldreansvarsloven",
 "version": "LBK-662-2026",
 "title": "Forældreansvarsloven § for §",
 "source": {
  "label": "LBK nr. 662 af 1. juli 2026",
  "url": "https://www.retsinformation.dk/eli/lta/2026/662"
 },
 "chapters": [
  "Introduktion",
  "Kap. 1 · Indledende bestemmelser",
  "Kap. 2 · Forældremyndighed",
  "Kap. 3 · Bopæl og varsling",
  "Kap. 4 · Samvær m.v.",
  "Kap. 5 · Midlertidige afgørelser",
  "Kap. 6 · Sagens behandling",
  "Kap. 7–7 b · Arbejde, tolke, børneydelse",
  "Kap. 8–9 · Internationalt og ikrafttræden",
  "Afslutning"
 ],
 "scenes": [
  {
   "id": "k0-start",
   "c": 0,
   "p": "Start",
   "t": "Sådan virker filmen",
   "s": "Filmen gennemgår hele forældreansvarsloven i den gældende udgave. Hver scene dækker en eller flere paragraffer. Først får du reglen forklaret i almindeligt sprog. Så kommer det, du skal være opmærksom på i en konkret sag. Under afspilleren står den ordrette lovtekst for scenen.",
   "pr": "Efter hvert kapitel stopper filmen med en quiz. Afspilningen står stille, til du har svaret. Din score står i indholdsfortegnelsen, og du kan hoppe til enhver paragraf derfra.",
   "v": {
    "k": "steps",
    "i": [
     "Forklaring i klart sprog",
     "I praksis: det skal du se efter",
     "Lovteksten ordret",
     "Quiz efter hvert kapitel"
    ]
   }
  },
  {
   "id": "k0-begreber",
   "c": 0,
   "p": "Begreber",
   "t": "Tre ting, du skal holde adskilt",
   "s": "Loven handler om tre forskellige ting. Forældremyndighed er retten og pligten til at træffe beslutninger om barnets personlige forhold. Bopæl er, hvor barnet bor, og bopælsforælderen bestemmer de overordnede ting i hverdagen. Samvær er barnets ret til kontakt med den forælder, det ikke bor hos.",
   "pr": "En forælder kan have del i forældremyndigheden uden at være bopælsforælder. Mange sager bliver lettere, når du spørger: Er uenigheden om myndighed, bopæl eller samvær?",
   "v": {
    "k": "ppl",
    "n": [
     [
      "m",
      "Mor",
      90,
      70,
      "p"
     ],
     [
      "f",
      "Far",
      310,
      70,
      "p"
     ],
     [
      "b",
      "Barn",
      200,
      190,
      "c"
     ]
    ],
    "l": [
     [
      "m",
      "b",
      "fm"
     ],
     [
      "f",
      "b",
      "fm"
     ],
     [
      "m",
      "b",
      "bo",
      "bopæl"
     ],
     [
      "f",
      "b",
      "sv",
      "samvær"
     ]
    ]
   }
  },
  {
   "id": "k0-version",
   "c": 0,
   "p": "Version",
   "t": "Brug den gældende udgave",
   "nyt": "Vigtigt",
   "s": "Mange links peger stadig på lovbekendtgørelse nr. 1768 fra 2020. Den er historisk. Den gældende udgave er nr. 662 af 1. juli 2026, og den samler otte ændringslove fra perioden 2020 til 2025.",
   "pr": "Scener med et turkis mærke viser regler, der er nye eller ændret siden 2020-udgaven. Dem skal du være særligt opmærksom på, hvis du har lært loven før.",
   "v": {
    "k": "split",
    "a": [
     "LBK 1768/2020 · historisk",
     [
      "Ingen regel om samarbejdschikane",
      "Kontaktbevarende samvær inden 3 uger",
      "Henviser til serviceloven ved anbringelse",
      "Ingen regel om krisecentre"
     ]
    ],
    "b": [
     "LBK 662/2026 · gældende",
     [
      "§ 4, stk. 2: chikane og fremmedgørelse",
      "§ 29 a: 4 uger og en 4-måneders frist",
      "§ 24: henviser til barnets lov",
      "§ 29 b, § 7 b, § 44 a og § 44 b er nye"
     ]
    ]
   }
  },
  {
   "id": "k1-p-1",
   "c": 1,
   "p": "§ 1",
   "t": "Barnets bedste kommer først",
   "s": "§ 1 er lovens grundprincip. I alt, hvad loven omfatter, kommer hensynet til barnets bedste og barnets ret til trivsel og beskyttelse i første række. Forældrenes ønsker er ikke ligegyldige, men de viger for barnet.",
   "pr": "En afgørelse skal vise, hvordan barnets bedste er vurderet. Forældrenes argumenter er relevante, når de siger noget om barnet.",
   "v": {
    "k": "word",
    "w": "Barnets bedste",
    "i": [
     "trivsel",
     "beskyttelse",
     "første række",
     "alle forhold"
    ]
   },
   "law": [
    [
     "§ 1",
     "I alle forhold, som er omfattet af denne lov, skal hensynet til barnets bedste og barnets ret til trivsel og beskyttelse komme i første række."
    ]
   ]
  },
  {
   "id": "k1-p-1-a",
   "c": 1,
   "p": "§ 1 a",
   "t": "Hvem er under forældremyndighed",
   "s": "Alle børn og unge under 18 år er under forældremyndighed. Den eneste undtagelse er, hvis den unge har indgået ægteskab.",
   "pr": "Forældremyndigheden ophører automatisk på 18-årsdagen. Derefter er der ikke noget at træffe afgørelse om efter denne lov.",
   "v": {
    "k": "big",
    "n": "18",
    "u": "år",
    "cap": "Under forældremyndighed til 18-årsdagen, medmindre den unge er gift."
   },
   "law": [
    [
     "§ 1 a",
     "Børn og unge under 18 år er under forældremyndighed, medmindre de har indgået ægteskab."
    ]
   ]
  },
  {
   "id": "k1-p-2",
   "c": 1,
   "p": "§ 2",
   "t": "Omsorg, respekt og revselsesforbud",
   "s": "Den, der har forældremyndigheden, skal drage omsorg for barnet og kan beslutte om dets personlige forhold ud fra barnets interesse og behov. Barnet har ret til omsorg og tryghed og må ikke udsættes for legemlig afstraffelse eller anden krænkende behandling. Forældrene må bruge barnets indtægter til dets underhold i passende omfang.",
   "pr": "Stk. 2 er revselsesforbuddet. Oplysninger om vold eller krænkende behandling er mere end en konflikt mellem forældrene. De er et spørgsmål om barnets beskyttelse og skal vurderes for sig.",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "Omsorg og tryghed"
     ],
     [
      "y",
      "Respekt for barnets person"
     ],
     [
      "n",
      "Legemlig afstraffelse"
     ],
     [
      "n",
      "Anden krænkende behandling"
     ]
    ]
   },
   "law": [
    [
     "§ 2",
     "Forældremyndighedens indehaver skal drage omsorg for barnet og kan træffe afgørelse om dets personlige forhold ud fra barnets interesse og behov.|Stk. 2. Barnet har ret til omsorg og tryghed. Det skal behandles med respekt for sin person og må ikke udsættes for legemlig afstraffelse eller anden krænkende behandling.|Stk. 3. Forældre, der har forældremyndigheden, kan anvende barnets indtægter til dets underhold i passende omfang og under hensyntagen til deres og barnets stilling."
    ]
   ]
  },
  {
   "id": "k1-p-3",
   "c": 1,
   "p": "§ 3",
   "t": "Hvem bestemmer hvad ved fælles myndighed",
   "s": "Ved fælles forældremyndighed kræver væsentlige beslutninger enighed. Bopælsforælderen bestemmer alene de overordnede forhold i hverdagen, herunder hvor i landet barnet skal bo. Har barnet delt bopæl, kræver også hverdagens overordnede forhold enighed. Er forældrene uenige om forældremyndigheden, skal begge give samtykke til, at barnet rejser ud af landet.",
   "pr": "Stk. 2 gælder kun, når der er uenighed om forældremyndigheden. Uden samtykke kræves en afgørelse efter § 17, stk. 1, 2. pkt. eller § 25.",
   "v": {
    "k": "split",
    "a": [
     "Kræver enighed",
     [
      "Væsentlige beslutninger",
      "Hverdagens overordnede forhold, når barnet har delt bopæl",
      "Udrejse, når der er uenighed om myndigheden"
     ]
    ],
    "b": [
     "Bopælsforælderen alene",
     [
      "Overordnede forhold i barnets daglige liv",
      "Hvor i landet barnet bor (husk varsel efter § 18)"
     ]
    ]
   },
   "law": [
    [
     "§ 3",
     "Har forældre fælles forældremyndighed, kræver væsentlige beslutninger vedrørende barnets forhold enighed mellem forældrene. Den forælder, som barnet har bopæl hos, kan træffe afgørelse om overordnede forhold i barnets daglige liv, herunder hvor i landet bopælen skal være. Har barnet delt bopæl efter § 18 a, stk. 1, 1. pkt., kræver afgørelse om overordnede forhold i barnets daglige liv efter 2. pkt. enighed mellem forældrene.|Stk. 2. Har forældre fælles forældremyndighed, og er de uenige om forældremyndigheden, skal begge give samtykke til, at barnet forlader landet, herunder udrejser til Grønland eller Færøerne, eller til, at barnets ophold i udlandet, i Grønland eller på Færøerne forlænges ud over det aftalte, forudsatte eller fastsatte, medmindre der foreligger en afgørelse efter § 17, stk. 1, 2. pkt., eller § 25."
    ]
   ]
  },
  {
   "id": "k1-p-4-stk-1",
   "c": 1,
   "p": "§ 4, stk. 1",
   "t": "Afgørelser skal beskytte barnet",
   "s": "Alle afgørelser skal træffes ud fra, hvad der er bedst for barnet. Familieretshuset og familieretten skal have fokus på, at afgørelsen sikrer barnets trivsel og beskytter det mod vold og anden behandling, der udsætter barnet for skade eller fare.",
   "pr": "Barnet behøver ikke selv at være udsat for vold. At være vidne til vold er nævnt direkte i loven som en fare, afgørelsen skal beskytte mod.",
   "v": {
    "k": "word",
    "w": "Beskyttelse",
    "i": [
     "trivsel",
     "vold",
     "vidne til vold",
     "skade eller fare"
    ]
   },
   "law": [
    [
     "§ 4",
     "Afgørelser efter loven skal træffes ud fra, hvad der er bedst for barnet. Familieretshuset og familieretten skal have fokus på, at afgørelser skal medvirke til at sikre barnets trivsel og beskytte barnet mod vold eller anden behandling, der udsætter barnet for skade eller fare, herunder at være vidne til vold."
    ]
   ]
  },
  {
   "id": "k1-p-4-stk-2",
   "c": 1,
   "p": "§ 4, stk. 2",
   "t": "Samarbejdschikane og forældrefremmedgørelse",
   "nyt": "Ny 1. januar 2025",
   "s": "Ved vurderingen af barnets bedste skal Familieretshuset og familieretten også inddrage barnets ret til begge forældre. Her indgår samarbejdschikane, herunder forældrefremmedgørelse, som er kommet til udtryk ved barnets adfærd. Chikanen skal have konsekvenser, hvis det er til barnets bedste.",
   "pr": "Læg mærke til kravet om barnets adfærd. Beskriv konkret, hvad barnet siger og gør. Konsekvensen er ikke en straf af forælderen. Den skal være det bedste for barnet.",
   "v": {
    "k": "steps",
    "i": [
     "Chikane eller fremmedgørelse",
     "Kommer til udtryk ved barnets adfærd",
     "Indgår i vurderingen af barnets bedste",
     "Konsekvens, hvis det er bedst for barnet"
    ]
   },
   "law": [
    [
     "§ 4, stk. 2",
     "Stk. 2. Familieretshuset og familieretten skal ved vurderingen af barnets bedste, jf. stk. 1, endvidere inddrage hensynet til barnets ret til begge forældre og i den forbindelse samarbejdschikane, herunder forældrefremmedgørelse, som er kommet til udtryk ved barnets adfærd. Samarbejdschikane, herunder forældrefremmedgørelse, tillægges konsekvenser, hvis dette er til barnets bedste."
    ]
   ]
  },
  {
   "id": "k1-p-4-a",
   "c": 1,
   "p": "§ 4 a",
   "t": "Formodningsreglen ved alvorlig kriminalitet",
   "nyt": "Udvidet 2021 og 2025",
   "s": "Er en forælder idømt ubetinget fængsel, anbringelse eller forvaring for bestemte alvorlige forbrydelser, kan forælderen ikke få forældremyndighed, bopæl, samvær eller anden kontakt, medmindre det er bedst for barnet. Udgangspunktet vendes altså om. Ved mistanke kan Familieretshuset indhente oplysninger fra Det Centrale Kriminalregister.",
   "pr": "Betinget straf er ikke omfattet. Tjek både sanktionen og straffelovens paragraf i dommen. Terror kom til i 2021, og § 244, stk. 2 om vold kom til med lov nr. 1688/2024 om bl.a. gentagen vold i nære relationer.",
   "v": {
    "k": "chk",
    "h": "Ubetinget fængsel, anbringelse (§ 68, 2. pkt., § 69) eller forvaring (§ 70) for:",
    "i": [
     [
      "i",
      "Terror, §§ 101 a, 114–114 j, § 136, stk. 2–3"
     ],
     [
      "i",
      "Incest (§ 210) og seksualforbrydelser, kap. 24 undtagen § 228"
     ],
     [
      "i",
      "Manddrab (§ 237), vold efter § 244, stk. 2, og §§ 245–246"
     ],
     [
      "i",
      "Menneskehandel (§ 262 a)"
     ]
    ]
   },
   "law": [
    [
     "§ 4 a",
     "Er en part idømt ubetinget fængselsstraf eller dømt til anbringelse efter straffelovens § 68, 2. pkt., eller § 69 eller til forvaring efter straffelovens § 70 for overtrædelse af straffelovens §§ 101 a eller 114-114 j, § 136, stk. 2, § 136, stk. 3, for så vidt angår billigelse af handlinger omfattet af straffelovens §§ 114-114 j, § 210, kapitel 24 bortset fra § 228, § 237, § 244, stk. 2, eller §§ 245-246 eller 262 a, kan der ikke, medmindre det er bedst for barnet, træffes afgørelse om følgende:|1) At parten har forældremyndigheden eller del i forældremyndigheden over barnet efter § 11, § 13, stk. 2, § 14, § 15, stk. 2 eller 3, eller §§ 15 a eller 26-28.|2) At barnet har bopæl hos parten efter § 17, stk. 1, eller § 26.|3) At barnet har samvær eller anden kontakt med parten efter §§ 20-21, 22, 29 eller 29 a.|Stk. 2. Familieretshuset kan, hvis der foreligger mistanke om, at en part er idømt ubetinget fængselsstraf eller dømt til anbringelse efter straffelovens § 68, 2. pkt., eller § 69 eller til forvaring efter straffelovens § 70 for kriminalitet omfattet af stk. 1, indhente oplysninger herom fra Det Centrale Kriminalregister."
    ]
   ]
  },
  {
   "id": "k1-p-5",
   "c": 1,
   "p": "§ 5",
   "t": "Barnets egne synspunkter",
   "s": "I alle forhold vedrørende barnet skal der tages hensyn til barnets egne synspunkter. Vægten afhænger af barnets alder og modenhed.",
   "pr": "§ 5 er princippet. Hvordan barnet inddrages i selve sagen, står i § 34, og barnets egen ret til at henvende sig står i § 35. Et barns ønske er vigtigt, men afgør ikke sagen alene.",
   "v": {
    "k": "word",
    "w": "Barnets stemme",
    "i": [
     "alder",
     "modenhed",
     "egne synspunkter",
     "§ 34 og § 35"
    ]
   },
   "law": [
    [
     "§ 5",
     "I alle forhold vedrørende barnet skal der tages hensyn til barnets egne synspunkter alt efter alder og modenhed."
    ]
   ]
  },
  {
   "id": "k1-quiz",
   "c": 1,
   "quiz": 1,
   "t": "Quiz · Kapitel 1",
   "qs": [
    {
     "id": "q1a",
     "q": "Hvornår gælder formodningsreglen i § 4 a?",
     "o": [
      "Når en forælder er sigtet for grov vold",
      "Når en forælder har fået betinget dom for vold",
      "Ved ubetinget fængsel, anbringelse eller forvaring for en af de nævnte forbrydelser",
      "Når der er indgivet politianmeldelse"
     ],
     "a": 2,
     "e": "Det kræver en dom med ubetinget fængsel, anbringelse efter § 68, 2. pkt. eller § 69, eller forvaring efter § 70 for en af de opregnede forbrydelser."
    },
    {
     "id": "q1b",
     "q": "Forældrene har fælles forældremyndighed, og barnet bor hos mor. Hvem bestemmer, hvor i landet barnet skal bo?",
     "o": [
      "Begge forældre i enighed",
      "Mor som bopælsforælder",
      "Familieretshuset",
      "Kommunen"
     ],
     "a": 1,
     "e": "Efter § 3, stk. 1, 2. pkt. afgør bopælsforælderen, hvor i landet bopælen skal være. Ved delt bopæl kræves enighed."
    },
    {
     "id": "q1c",
     "q": "Efter § 4, stk. 2 skal forældrefremmedgørelse være kommet til udtryk ved…",
     "o": [
      "den anden forælders beskrivelse",
      "barnets adfærd",
      "en politirapport",
      "en afgørelse fra familieretten"
     ],
     "a": 1,
     "e": "Loven kræver, at samarbejdschikanen eller fremmedgørelsen er kommet til udtryk ved barnets adfærd."
    }
   ]
  },
  {
   "id": "k2-p-6",
   "c": 2,
   "p": "§ 6",
   "t": "Gifte forældre",
   "s": "Er forældrene gift ved fødslen, eller gifter de sig senere, har de fælles forældremyndighed. Har de været gift inden for de sidste 10 måneder før fødslen, har de også fælles myndighed. Er de separeret ved fødslen, har moren forældremyndigheden alene, medmindre ægtefællen anerkendes som far eller medmor, eller forældrene har afgivet en omsorgs- og ansvarserklæring.",
   "pr": "§ 48 begrænser tidsmæssigt: stk. 2, nr. 1 og stk. 3 gælder kun for børn født 1. oktober 2007 eller senere.",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "Gift ved fødslen eller senere: fælles"
     ],
     [
      "y",
      "Gift inden for 10 måneder før fødslen: fælles"
     ],
     [
      "i",
      "Separeret ved fødslen: mor alene, medmindre anerkendelse eller erklæring"
     ]
    ]
   },
   "law": [
    [
     "§ 6",
     "Er forældre gift med hinanden ved barnets fødsel, eller indgår de senere ægteskab, har de fælles forældremyndighed.|Stk. 2. Er ægtefællerne separeret ved barnets fødsel, har moderen dog forældremyndigheden alene, medmindre|1) den separerede mand eller medmor ifølge anerkendelse eller afgørelse anses som henholdsvis barnets far eller medmor eller|2) forældrene har afgivet erklæring efter § 7, stk. 1, nr. 1, jf. dog § 7, stk. 2.|Stk. 3. Har forældrene været gift med hinanden inden for de sidste 10 måneder før barnets fødsel, har de fælles forældremyndighed."
    ]
   ]
  },
  {
   "id": "k2-p-7",
   "c": 2,
   "p": "§ 7",
   "t": "Ugifte forældre",
   "s": "Ugifte forældre har fælles forældremyndighed, hvis de har afgivet en omsorgs- og ansvarserklæring efter børneloven, har anmeldt en aftale efter § 9, eller hvis manden er registreret som far efter børnelovens § 1 a. De har også fælles myndighed, hvis faderskab eller medmoderskab er fastslået, og forældrene har eller har haft fælles folkeregisteradresse inden for 10 måneder før fødslen. I alle andre tilfælde har moren myndigheden alene.",
   "pr": "Stk. 2: En erklæring giver ikke fælles myndighed, hvis betingelserne for dansk kompetence i retsplejelovens § 448 g ikke er opfyldt. Tjek det i sager med udenlandsk tilknytning.",
   "v": {
    "k": "chk",
    "h": "Fælles forældremyndighed, hvis:",
    "i": [
     [
      "y",
      "Omsorgs- og ansvarserklæring efter børneloven"
     ],
     [
      "y",
      "Anmeldt aftale efter § 9"
     ],
     [
      "y",
      "Far registreret efter børnelovens § 1 a"
     ],
     [
      "y",
      "Fælles folkeregisteradresse inden for 10 mdr. før fødslen"
     ],
     [
      "n",
      "Ellers: mor har myndigheden alene"
     ]
    ]
   },
   "law": [
    [
     "§ 7",
     "Forældre, der ikke er gift med hinanden, har fælles forældremyndighed, hvis|1) de efter børnelovens § 2, stk. 1, § 3 b, stk. 1, § 14, stk. 1, 3 eller 6, eller § 19, jf. § 14, stk. 1, 3 eller 6, har afgivet erklæring om, at de sammen vil varetage omsorgen og ansvaret for barnet,|2) de har indgået aftale om fælles forældremyndighed efter § 9 eller|3) manden efter børnelovens § 1 a er registreret som far til barnet.|Stk. 2. Dette gælder dog ikke, hvis erklæringen i stk. 1, nr. 1, er afgivet, uden at betingelserne i § 448 g i retsplejeloven for her i landet at behandle spørgsmålet om forældremyndighed er opfyldt.|Stk. 3. Anses en mand for far eller en kvinde som medmor til barnet ifølge anerkendelse eller afgørelse, har forældrene fælles forældremyndighed, hvis de har eller har haft fælles folkeregisteradresse inden for de sidste 10 måneder før barnets fødsel.|Stk. 4. I andre tilfælde end nævnt i stk. 1-3 har moderen forældremyndigheden alene."
    ]
   ]
  },
  {
   "id": "k2-p-7-a",
   "c": 2,
   "p": "§ 7 a",
   "t": "Adoption",
   "s": "Forældre, der har adopteret sammen, har fælles forældremyndighed. Den, der adopterer alene, har myndigheden alene. Ved stedbarnsadoption efter adoptionslovens § 5 a, stk. 1 har begge fælles myndighed. Reglerne gælder også for godkendte adoptanter i perioden fra et udenlandsk barn ankommer, til adoptionen er bevilget, og mens et barn er midlertidigt placeret efter adoptionslovens § 32 a.",
   "pr": "I en sag om et adopteret barn skal du kende datoen for bevillingen. Før bevillingen følger myndigheden af stk. 4 eller stk. 5.",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "Adopteret sammen: fælles"
     ],
     [
      "i",
      "Adopteret alene: alene"
     ],
     [
      "y",
      "Stedbarnsadoption: fælles"
     ],
     [
      "i",
      "Udenlandsk adoption før bevilling og midlertidig placering: stk. 1 og 2 gælder"
     ]
    ]
   },
   "law": [
    [
     "§ 7 a",
     "Forældre, der sammen har adopteret et barn, har fælles forældremyndighed.|Stk. 2. En forælder, der har adopteret et barn alene, har forældremyndigheden alene, jf. dog stk. 3.|Stk. 3. Forældre har fælles forældremyndighed, når den ene har adopteret den andens barn efter § 5 a, stk. 1, i adoptionsloven.|Stk. 4. Stk. 1 og 2 gælder også for ansøgere med godkendelse som adoptant, der adopterer et barn fra et andet land, i perioden fra barnets ankomst her til landet, og til Familieretshuset har meddelt bevilling til adoption efter § 1 i adoptionsloven.|Stk. 5. Stk. 1 og 2 gælder også for ansøgere med godkendelse som adoptant, der har fået et barn placeret efter § 32 a i adoptionsloven, så længe barnet er midlertidigt placeret."
    ]
   ]
  },
  {
   "id": "k2-p-7-b",
   "c": 2,
   "p": "§ 7 b",
   "t": "Surrogataftaler og medfar",
   "nyt": "Ny 1. januar 2025",
   "s": "Når moderskab, faderskab, medmoderskab eller medfaderskab registreres, anerkendes eller fastslås efter børnelovens kapitel 1 b, 5 b eller 5 c, har forældrene fælles forældremyndighed. Har barnet kun én forælder, har denne myndigheden alene.",
   "pr": "Reglen kom med lov nr. 1687/2024 om familiedannelse ved surrogataftaler og medfar som retlig forælder. Slå op i børneloven, hvilket kapitel forældreskabet er fastslået efter.",
   "v": {
    "k": "ppl",
    "n": [
     [
      "a",
      "Forælder 1",
      90,
      70,
      "p"
     ],
     [
      "b",
      "Forælder 2",
      310,
      70,
      "p"
     ],
     [
      "c",
      "Barn",
      200,
      190,
      "c"
     ]
    ],
    "l": [
     [
      "a",
      "c",
      "fm"
     ],
     [
      "b",
      "c",
      "fm"
     ]
    ]
   },
   "law": [
    [
     "§ 7 b",
     "Ved registrering og anerkendelse af og afgørelse om moderskab, faderskab, medmoderskab og medfaderskab efter børnelovens kapitel 1 b, 5 b og 5 c har forældrene fælles forældremyndighed. Har barnet kun én forælder, har denne forældremyndigheden alene."
    ]
   ]
  },
  {
   "id": "k2-p-8",
   "c": 2,
   "p": "§ 8",
   "t": "Fælles myndighed fortsætter efter brud",
   "s": "Har forældrene fælles forældremyndighed, fortsætter den, selv om de flytter fra hinanden, bliver separeret eller skilt, eller ægteskabet omstødes.",
   "pr": "Et samlivsbrud ændrer ikke forældremyndigheden. Det kræver en aftale efter § 10 eller en afgørelse efter § 11.",
   "v": {
    "k": "ppl",
    "n": [
     [
      "m",
      "Mor",
      90,
      70,
      "p"
     ],
     [
      "f",
      "Far",
      310,
      70,
      "p"
     ],
     [
      "b",
      "Barn",
      200,
      190,
      "c"
     ]
    ],
    "l": [
     [
      "m",
      "f",
      "x",
      "samlivsbrud"
     ],
     [
      "m",
      "b",
      "fm"
     ],
     [
      "f",
      "b",
      "fm"
     ]
    ]
   },
   "law": [
    [
     "§ 8",
     "Har forældre fælles forældremyndighed, fortsætter denne, selv om de har ophævet samlivet eller er blevet separeret eller skilt, eller deres ægteskab er blevet omstødt."
    ]
   ]
  },
  {
   "id": "k2-pp-9-10-a",
   "c": 2,
   "p": "§§ 9–10 a",
   "t": "Aftaler om forældremyndighed",
   "s": "Forældre kan aftale fælles forældremyndighed (§ 9). Forældre med fælles myndighed, der ikke bor sammen, kan aftale, at den ene får den alene (§ 10). Aftalen er først gyldig, når den er anmeldt til Familieretshuset, eller til retten, hvis sagen verserer der. Efter § 10 a er aftalen ugyldig, hvis den er indgået før fødslen, eller hvis den er betinget eller tidsbegrænset.",
   "pr": "En aftale som 'far har myndigheden, indtil barnet begynder i skole' er ugyldig, fordi den er tidsbegrænset.",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "Aftale mellem forældrene"
     ],
     [
      "y",
      "Anmeldt til Familieretshuset (eller retten)"
     ],
     [
      "n",
      "Indgået før barnets fødsel"
     ],
     [
      "n",
      "Betinget eller tidsbegrænset"
     ]
    ]
   },
   "law": [
    [
     "§ 9",
     "Forældre kan aftale, at de skal have fælles forældremyndighed. Aftalen skal anmeldes til Familieretshuset for at være gyldig. Er en sag om forældremyndighed indbragt for retten, kan anmeldelse ske hertil."
    ],
    [
     "§ 10",
     "Forældre, der har fælles forældremyndighed, og som ikke lever sammen, kan aftale, at en af dem skal have forældremyndigheden alene. Aftalen skal anmeldes til Familieretshuset for at være gyldig. Er en sag om forældremyndighed indbragt for retten, kan anmeldelse ske hertil."
    ],
    [
     "§ 10 a",
     "En aftale om forældremyndighed efter §§ 9 eller 10 er ikke gyldig, hvis den er indgået inden barnets fødsel, eller aftalen er betinget eller tidsbegrænset."
    ]
   ]
  },
  {
   "id": "k2-p-11",
   "c": 2,
   "p": "§ 11",
   "t": "Uenighed om fælles myndighed",
   "s": "Er forældre med fælles myndighed, der ikke bor sammen, uenige, træffes der afgørelse om, hvorvidt den fælles myndighed skal fortsætte, eller om den ene skal have den alene. Den fælles myndighed kan kun ophæves, hvis der er holdepunkter for, at forældrene ikke kan samarbejde om barnets forhold til barnets bedste.",
   "pr": "Udgangspunktet er fælles myndighed. At forældrene er uvenner, er ikke nok. Se på, om de kan samarbejde om barnet. I sager efter § 4 a gælder kravet om manglende samarbejdsevne ikke (3. pkt.).",
   "v": {
    "k": "steps",
    "i": [
     "Uenighed om forældremyndigheden",
     "Kan de samarbejde om barnet til barnets bedste?",
     "Ja: fælles myndighed fortsætter",
     "Nej: én får forældremyndigheden alene"
    ]
   },
   "law": [
    [
     "§ 11",
     "Er forældre, der har fælles forældremyndighed, og som ikke lever sammen, ikke enige om forældremyndigheden, træffes der afgørelse om, hvorvidt den fælles forældremyndighed skal fortsætte, eller om en af dem skal have forældremyndigheden alene. Den fælles forældremyndighed kan kun ophæves, hvis der er holdepunkter for at antage, at forældrene ikke kan samarbejde om barnets forhold til barnets bedste. Dette gælder dog ikke i sager omfattet af § 4 a."
    ]
   ]
  },
  {
   "id": "k2-p-12",
   "c": 2,
   "p": "§ 12",
   "t": "Når gifte forældre flytter sammen igen",
   "s": "Har den ene fået myndigheden alene ved aftale efter § 10 eller afgørelse efter § 11, genopstår den fælles myndighed automatisk, hvis gifte, også separerede, forældre genoptager eller fortsætter samlivet.",
   "pr": "Reglen gælder kun gifte og separerede forældre. For ugifte, der flytter sammen igen, skal der en ny aftale til efter § 9.",
   "v": {
    "k": "ppl",
    "n": [
     [
      "m",
      "Mor",
      90,
      70,
      "p"
     ],
     [
      "f",
      "Far",
      310,
      70,
      "p"
     ],
     [
      "b",
      "Barn",
      200,
      190,
      "c"
     ]
    ],
    "l": [
     [
      "m",
      "f",
      "thin",
      "gift, samlever igen"
     ],
     [
      "m",
      "b",
      "fm"
     ],
     [
      "f",
      "b",
      "fm"
     ]
    ]
   },
   "law": [
    [
     "§ 12",
     "Er der indgået aftale efter § 10 eller truffet afgørelse efter § 11 om, at en af forældrene skal have forældremyndigheden alene, genindtræder den fælles forældremyndighed, hvis gifte, herunder separerede, forældre genoptager eller fortsætter samlivet."
    ]
   ]
  },
  {
   "id": "k2-p-13",
   "c": 2,
   "p": "§ 13",
   "t": "Overførsel af forældremyndighed",
   "nyt": "Stk. 3 ændret",
   "s": "Forældre kan aftale at overføre myndigheden fra den ene til den anden. Det kræver anmeldelse. Myndigheden kan også overføres til andre end forældrene, fx et ægtepar, et samlevende par eller en forælder og dennes nye partner. Det kræver godkendelse fra Familieretshuset eller familieretten. Aftalen er ugyldig, hvis den er indgået før fødslen eller er betinget eller tidsbegrænset. Betales der vederlag, er en aftale med andre ugyldig, medmindre den alligevel er bedst for barnet.",
   "pr": "Forskellen er vigtig: mellem forældre er anmeldelse nok, til andre kræves godkendelse. Før godkendelse skal du indhente en erklæring fra den forælder, der ikke har del i myndigheden (§ 36).",
   "v": {
    "k": "split",
    "a": [
     "Stk. 1 · mellem forældre",
     [
      "Aftale",
      "Anmeldelse til Familieretshuset"
     ]
    ],
    "b": [
     "Stk. 2 · til andre",
     [
      "Aftale",
      "Godkendelse af Familieretshuset eller familieretten",
      "Erklæring fra forælder efter § 36"
     ]
    ]
   },
   "law": [
    [
     "§ 13",
     "Forældre kan aftale, at forældremyndigheden skal overføres fra den ene forælder til den anden. Aftalen skal anmeldes til Familieretshuset for at være gyldig. Er en sag om forældremyndighed indbragt for familieretten, kan anmeldelse ske hertil.|Stk. 2. Forældremyndigheden kan ved aftale godkendt af Familieretshuset eller familieretten overføres til andre end forældre. Forældremyndigheden kan overføres til et ægtepar eller et samlevende par i forening, herunder til den ene forælder og dennes ægtefælle eller samlever. Endvidere kan en forældremyndighedsindehaver og en af barnets forældre aftale at have fælles forældremyndighed.|Stk. 3. En aftale efter stk. 1 og 2 er ikke gyldig, hvis aftalen er indgået inden barnets fødsel, eller aftalen er betinget eller tidsbegrænset. Endvidere er en aftale efter stk. 2 ikke gyldig, hvis der ydes vederlag eller betaling for tabt arbejdsfortjeneste til forældremyndighedens indehaver, medmindre aftalen trods ydelse af vederlag eller betaling for tabt arbejdsfortjeneste kan antages at være bedst for barnet."
    ]
   ]
  },
  {
   "id": "k2-p-14",
   "c": 2,
   "p": "§ 14",
   "t": "Forælder uden myndighed søger",
   "s": "En forælder, der ikke har forældremyndigheden, kan anmode om fælles myndighed eller om at få den overført. En aftale efter § 13, stk. 2, og afgørelser efter §§ 15 og 15 a kan ændres.",
   "pr": "En anmodning efter stk. 2 kan ikke afvises med den begrundelse, at forholdene ikke har ændret sig væsentligt (§ 39, 2. pkt.).",
   "v": {
    "k": "ppl",
    "n": [
     [
      "m",
      "Mor",
      90,
      70,
      "p"
     ],
     [
      "f",
      "Far",
      310,
      70,
      "p"
     ],
     [
      "b",
      "Barn",
      200,
      190,
      "c"
     ]
    ],
    "l": [
     [
      "m",
      "b",
      "fm"
     ],
     [
      "f",
      "b",
      "q",
      "anmoder"
     ]
    ]
   },
   "law": [
    [
     "§ 14",
     "Efter anmodning fra en forælder, der ikke har forældremyndigheden, kan det bestemmes, at der skal være fælles forældremyndighed, eller at forældremyndigheden skal overføres til denne.|Stk. 2. En aftale efter § 13, stk. 2, og en afgørelse efter §§ 15 og 15 a kan ændres."
    ]
   ]
  },
  {
   "id": "k2-p-15",
   "c": 2,
   "p": "§ 15",
   "t": "Når en forælder dør",
   "s": "Havde forældrene fælles myndighed, får den efterlevende den alene. Boede barnet ikke hos den efterlevende, kan andre anmode om myndigheden. Havde den afdøde myndigheden alene, træffes der afgørelse, og både den efterlevende og andre kan søge. Det samme gælder, hvis begge forældre dør. Myndigheden kan i de sager gives til et par i forening.",
   "pr": "Før afgørelse efter stk. 3 skal der indhentes en erklæring fra den forælder, der ikke har del i myndigheden (§ 36).",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "Fælles myndighed: efterlevende får den alene"
     ],
     [
      "i",
      "Barnet boede ikke hos efterlevende: andre kan anmode"
     ],
     [
      "i",
      "Afdøde havde den alene: der træffes afgørelse"
     ],
     [
      "i",
      "Begge forældre døde: der træffes afgørelse"
     ]
    ]
   },
   "law": [
    [
     "§ 15",
     "Dør en forælder, og havde forældrene ved dødsfaldet fælles forældremyndighed, har den efterlevende forælder forældremyndigheden alene, jf. dog stk. 2 og § 15 a, stk. 1.|Stk. 2. Dør en forælder, og havde forældrene ved dødsfaldet fælles forældremyndighed, kan andre i forbindelse med dødsfaldet anmode om forældremyndigheden, hvis barnet ved dødsfaldet ikke havde bopæl hos den efterlevende forælder.|Stk. 3. Dør en forælder, der har forældremyndigheden alene, træffes der afgørelse om, hvem der skal have forældremyndigheden. En efterlevende forælder og andre kan anmode om forældremyndigheden. Er der fælles forældremyndighed, og dør begge forældre, finder 1. og 2. pkt. tilsvarende anvendelse.|Stk. 4. I sager omfattet af stk. 2 og 3 finder § 13, stk. 2, 2. pkt., tilsvarende anvendelse."
    ]
   ]
  },
  {
   "id": "k2-p-15-a",
   "c": 2,
   "p": "§ 15 a",
   "t": "Når en forælder har forvoldt den andens død",
   "s": "Har den ene forælder forvoldt den andens død, overgår myndigheden ikke automatisk. Der træffes afgørelse om, hvorvidt den skal blive hos den efterlevende, eller om en anden skal have den. Havde den afdøde myndigheden alene, gælder § 15, stk. 3.",
   "pr": "Se også § 4 a om manddrab. Afgørelsen kan ændres efter § 14, stk. 2.",
   "v": {
    "k": "steps",
    "i": [
     "Den ene forælder har forvoldt den andens død",
     "Ingen automatisk overgang",
     "Afgørelse: efterlevende eller en anden",
     "Kan gives til et par i forening"
    ]
   },
   "law": [
    [
     "§ 15 a",
     "Har den ene forælder forvoldt den anden forælders død, og havde forældrene ved dødsfaldet fælles forældremyndighed, eller havde den efterlevende forælder forældremyndigheden alene, træffes der afgørelse om, hvorvidt forældremyndigheden skal forblive hos den efterlevende forælder, eller om en anden skal have forældremyndigheden.|Stk. 2. Hvis den ene forælder har forvoldt den anden forælders død, og den afdøde forælder havde forældremyndigheden alene, finder § 15, stk. 3, tilsvarende anvendelse.|Stk. 3. I sager omfattet af stk. 1 finder § 13, stk. 2, 2. pkt., tilsvarende anvendelse."
    ]
   ]
  },
  {
   "id": "k2-p-16",
   "c": 2,
   "p": "§ 16",
   "t": "Ønske om myndighed efter død",
   "s": "Den, der har forældremyndigheden, kan tilkendegive, hvem der bør have den efter sin død.",
   "pr": "En tilkendegivelse er et ønske, som indgår i vurderingen af barnets bedste. Den bestemmer ikke udfaldet.",
   "v": {
    "k": "big",
    "n": "§ 16",
    "u": "",
    "cap": "Et skriftligt ønske om, hvem der skal have forældremyndigheden efter dødsfald."
   },
   "law": [
    [
     "§ 16",
     "Indehavere af forældremyndigheden kan tilkendegive, hvem der efter deres død bør have tillagt forældremyndigheden."
    ]
   ]
  },
  {
   "id": "k2-quiz",
   "c": 2,
   "quiz": 1,
   "t": "Quiz · Kapitel 2",
   "qs": [
    {
     "id": "q2a",
     "q": "Ugifte forældre har ikke afgivet erklæring, men faderskabet er anerkendt, og de havde fælles folkeregisteradresse 6 måneder før fødslen. Hvem har forældremyndigheden?",
     "o": [
      "Mor alene",
      "Fælles",
      "Far alene",
      "Det afgør Familieretshuset"
     ],
     "a": 1,
     "e": "Efter § 7, stk. 3 har de fælles myndighed ved fælles folkeregisteradresse inden for 10 måneder før fødslen."
    },
    {
     "id": "q2b",
     "q": "Forældrene aftaler før fødslen, at de skal have fælles forældremyndighed. Er aftalen gyldig?",
     "o": [
      "Ja, hvis den anmeldes",
      "Nej, efter § 10 a",
      "Ja, hvis den godkendes",
      "Kun hvis de er gift"
     ],
     "a": 1,
     "e": "En aftale efter §§ 9 eller 10 er ugyldig, hvis den er indgået før barnets fødsel."
    },
    {
     "id": "q2c",
     "q": "Hvad skal der til for at ophæve fælles forældremyndighed efter § 11 (uden for § 4 a)?",
     "o": [
      "At den ene forælder ønsker det",
      "At forældrene er i konflikt",
      "Holdepunkter for, at de ikke kan samarbejde om barnet til barnets bedste",
      "At barnet ønsker det"
     ],
     "a": 2,
     "e": "Tærsklen er manglende evne til at samarbejde om barnets forhold, vurderet ud fra barnets bedste."
    },
    {
     "id": "q2d",
     "q": "Hvad kræver en overførsel af forældremyndigheden til en moster efter § 13, stk. 2?",
     "o": [
      "Kun anmeldelse",
      "Godkendelse fra Familieretshuset eller familieretten",
      "Dom",
      "Ingenting, hvis forældrene er enige"
     ],
     "a": 1,
     "e": "Overførsel til andre end forældre kræver godkendelse. Mellem forældre er anmeldelse nok."
    }
   ]
  },
  {
   "id": "k3-p-17",
   "c": 3,
   "p": "§ 17",
   "t": "Afgørelse om barnets bopæl",
   "s": "Har forældrene fælles myndighed og er uenige om, hvor barnet skal bo, kan der træffes afgørelse. Afgørelsen kan også gå ud på, at barnet kan bo hos en forælder, der har eller ønsker bopæl i udlandet, i Grønland eller på Færøerne. En aftale eller afgørelse om bopæl kan ændres.",
   "pr": "Afgørelse efter stk. 1, 2. pkt. er undtagelsen i § 3, stk. 2. Med den kan barnet rejse ud uden den anden forælders samtykke.",
   "v": {
    "k": "ppl",
    "n": [
     [
      "m",
      "Mor",
      90,
      70,
      "p"
     ],
     [
      "f",
      "Far",
      310,
      70,
      "p"
     ],
     [
      "b",
      "Barn",
      200,
      190,
      "c"
     ]
    ],
    "l": [
     [
      "m",
      "b",
      "fm"
     ],
     [
      "f",
      "b",
      "fm"
     ],
     [
      "m",
      "b",
      "q",
      "bopæl?"
     ],
     [
      "f",
      "b",
      "q",
      "bopæl?"
     ]
    ]
   },
   "law": [
    [
     "§ 17",
     "Har forældre fælles forældremyndighed, og er de ikke enige om, hos hvem af dem barnet skal have bopæl, kan der træffes afgørelse herom. Der kan herunder træffes afgørelse om, at barnet kan have bopæl hos en forælder, der har eller ønsker at få bopæl i udlandet, i Grønland eller på Færøerne.|Stk. 2. En aftale eller afgørelse om barnets bopæl efter stk. 1 kan ændres."
    ]
   ]
  },
  {
   "id": "k3-p-18",
   "c": 3,
   "p": "§ 18",
   "t": "Varsel om flytning",
   "s": "En forælder, der vil flytte sig selv eller barnet til et andet sted i landet eller til udlandet, skal underrette den anden forælder senest 6 uger før flytningen.",
   "pr": "Pligten gælder begge forældre og både egen og barnets flytning. Varslet giver den anden forælder tid til at søge om ændring af bopæl eller samvær.",
   "v": {
    "k": "big",
    "n": "6",
    "u": "uger",
    "cap": "Varsel til den anden forælder før flytning i Danmark eller til udlandet."
   },
   "law": [
    [
     "§ 18",
     "En forælder, der vil ændre sin eller barnets bopæl til et andet sted her i landet eller i udlandet, skal underrette den anden forælder herom senest 6 uger før flytningen."
    ]
   ]
  },
  {
   "id": "k3-p-18-a",
   "c": 3,
   "p": "§ 18 a",
   "t": "Delt bopæl",
   "s": "Forældre med fælles myndighed kan aftale delt bopæl. Aftalen ophører, når den ene meddeler det til den anden. Barnet har dog fortsat delt bopæl, til der er en ny aftale eller afgørelse om bopæl eller samvær. Bryder en forælder en aftalt betingelse om, hvor forældrene skal bo, og den anden opsiger inden 1 måned efter flytningen, bor barnet hos den, der har overholdt aftalen. Aftalen kan registreres i CPR.",
   "pr": "Delt bopæl kan kun aftales. Tvungen delt bopæl blev afskaffet med lov nr. 962/2020. Husk § 3: ved delt bopæl kræver hverdagens overordnede beslutninger enighed.",
   "v": {
    "k": "tl",
    "i": [
     [
      "Aftale",
      "om delt bopæl, kan registreres i CPR"
     ],
     [
      "Opsigelse",
      "en forælder meddeler ophør"
     ],
     [
      "Mellemperiode",
      "barnet har stadig delt bopæl"
     ],
     [
      "Afslutning",
      "ny aftale eller afgørelse efter §§ 17, 26, 21, 29 eller 29 a"
     ]
    ]
   },
   "law": [
    [
     "§ 18 a",
     "Forældre, der har fælles forældremyndighed, kan aftale, at barnet har delt bopæl, jf. § 3, stk. 1, 3. pkt. Aftalen ophører, når den ene forælder meddeler ophøret til den anden forælder. Barnet har dog efter ophør af aftalen fortsat delt bopæl, jf. § 3, stk. 1, 3. pkt., indtil forældrene har indgået en aftale om barnets bopæl eller samvær eller der er truffet afgørelse om barnets bopæl efter §§ 17 eller 26 eller om samvær efter §§ 21, 29 eller 29 a, jf. dog stk. 2.|Stk. 2. Har en forælder bragt en aftale om delt bopæl til ophør, som følge af at den anden forælder ved en flytning af sin bopæl ikke har overholdt betingelser i aftalen om den geografiske placering af forældrenes bopæle, og sker ophøret inden for 1 måned efter den anden forælders flytning, har barnet bopæl hos den forælder, der har overholdt aftalen.|Stk. 3. En aftale om delt bopæl efter stk. 1 kan registreres i CPR ved anmeldelse af aftalen til Familieretshuset. Registreringen fjernes, hvis en forælder foretager anmeldelse herom til Familieretshuset."
    ]
   ]
  },
  {
   "id": "k3-quiz",
   "c": 3,
   "quiz": 1,
   "t": "Quiz · Kapitel 3",
   "qs": [
    {
     "id": "q3a",
     "q": "Hvor lang tid før en flytning skal den anden forælder have besked?",
     "o": [
      "2 uger",
      "4 uger",
      "6 uger",
      "3 måneder"
     ],
     "a": 2,
     "e": "§ 18: senest 6 uger før flytningen."
    },
    {
     "id": "q3b",
     "q": "Kan Familieretshuset træffe afgørelse om delt bopæl mod den ene forælders vilje?",
     "o": [
      "Ja, hvis det er bedst for barnet",
      "Nej, delt bopæl kræver en aftale",
      "Ja, men kun midlertidigt",
      "Kun familieretten kan"
     ],
     "a": 1,
     "e": "§ 18 a giver kun mulighed for delt bopæl ved aftale. Tvungen delt bopæl blev afskaffet i 2020."
    },
    {
     "id": "q3c",
     "q": "En forælder bryder aftalens krav om, at begge bor i samme kommune. Hvor hurtigt skal den anden opsige aftalen, for at barnet får bopæl hos den, der overholdt den?",
     "o": [
      "Inden 14 dage",
      "Inden 1 måned efter flytningen",
      "Inden 6 uger",
      "Der er ingen frist"
     ],
     "a": 1,
     "e": "§ 18 a, stk. 2: ophøret skal ske inden for 1 måned efter den anden forælders flytning."
    }
   ]
  },
  {
   "id": "k4-p-19",
   "c": 4,
   "p": "§ 19",
   "t": "Barnets ret til samvær",
   "s": "Barnet har ret til samvær med den forælder, det ikke bor hos. Begge forældre har ansvar for, at samværet sker, og for transporten. Samværsforælderen kan anmode om samvær. Er der intet eller kun meget lidt samvær, kan bopælsforælderen bede Familieretshuset indkalde den anden forælder til et møde om samværet.",
   "pr": "Samvær er barnets ret, ikke forælderens. Transportudgifter kan fordeles efter reglerne i forældreansvarsbekendtgørelsen, og § 38 giver adgang til økonomiske oplysninger.",
   "v": {
    "k": "ppl",
    "n": [
     [
      "m",
      "Bopæl",
      90,
      70,
      "p"
     ],
     [
      "f",
      "Samvær",
      310,
      70,
      "p"
     ],
     [
      "b",
      "Barn",
      200,
      190,
      "c"
     ]
    ],
    "l": [
     [
      "m",
      "b",
      "bo"
     ],
     [
      "f",
      "b",
      "sv",
      "barnets ret"
     ]
    ]
   },
   "law": [
    [
     "§ 19",
     "Barnets forbindelse med begge forældre søges bevaret ved, at barnet har ret til samvær med den forælder, som det ikke har bopæl hos.|Stk. 2. Forældrene har et fælles ansvar for, at barnet har samvær med den forælder, som det ikke har bopæl hos, og for transporten af barnet i forbindelse med samvær.|Stk. 3. Den forælder, som barnet ikke har bopæl hos, kan anmode om samvær.|Stk. 4. Er der ikke eller kun i yderst begrænset omfang samvær, kan den forælder, som barnet har bopæl hos, anmode Familieretshuset om at indkalde den anden forælder til et møde om samværet."
    ]
   ]
  },
  {
   "id": "k4-p-20",
   "c": 4,
   "p": "§ 20",
   "t": "Samvær med andre end forældre",
   "s": "Barnets nærmeste pårørende, som barnet er knyttet til, kan få fastsat samvær i to situationer. Den første er, når en eller begge forældre er døde, eller en forælder er ukendt. Den anden er, når der ikke eller kun i yderst begrænset omfang er samvær med den forælder, barnet ikke bor hos.",
   "pr": "Typisk er det bedsteforældre. Før afgørelse efter stk. 2 skal du indhente en erklæring fra den forælder, der ikke har samvær (§ 36, stk. 2).",
   "v": {
    "k": "ppl",
    "n": [
     [
      "m",
      "Bopæl",
      90,
      60,
      "p"
     ],
     [
      "g",
      "Pårørende",
      320,
      60,
      "o"
     ],
     [
      "f",
      "Forælder",
      320,
      180,
      "p"
     ],
     [
      "b",
      "Barn",
      170,
      180,
      "c"
     ]
    ],
    "l": [
     [
      "m",
      "b",
      "bo"
     ],
     [
      "f",
      "b",
      "x",
      "intet samvær"
     ],
     [
      "g",
      "b",
      "sv",
      "§ 20"
     ]
    ]
   },
   "law": [
    [
     "§ 20",
     "Er en forælder eller begge forældre døde, eller er en forælder ukendt, kan der efter anmodning fastsættes samvær med barnets nærmeste pårørende, som det er knyttet til.|Stk. 2. Er der ikke eller kun i yderst begrænset omfang samvær med den forælder, barnet ikke har bopæl hos, kan der efter anmodning fastsættes samvær med barnets nærmeste pårørende, som det er knyttet til."
    ]
   ]
  },
  {
   "id": "k4-p-20-a",
   "c": 4,
   "p": "§ 20 a",
   "t": "Adopterede børn og oprindelig familie",
   "s": "Er barnet adopteret, kan barnets oprindelige slægtninge anmode om samvær eller anden kontakt, især hvis barnet havde kontakt med dem før adoptionen. Det gælder også, når barnet er midlertidigt placeret efter adoptionslovens § 32 a.",
   "pr": "Efter overgangsreglen i lov nr. 494/2009 gælder § 20 a kun for adoptioner gennemført efter 1. oktober 2009.",
   "v": {
    "k": "steps",
    "i": [
     "Barnet er adopteret",
     "Oprindelig slægtning anmoder",
     "Især hvis der var kontakt før",
     "Samvær eller anden kontakt kan fastsættes"
    ]
   },
   "law": [
    [
     "§ 20 a",
     "Er barnet adopteret, kan der efter anmodning fra barnets oprindelige slægtninge fastsættes samvær eller anden form for kontakt med disse, navnlig hvis barnet forud for adoptionen havde samvær eller anden form for kontakt med den, som anmoder om fastsættelse af samvær m.v.|Stk. 2. Stk. 1 gælder også ved midlertidig placering efter § 32 a i adoptionsloven."
    ]
   ]
  },
  {
   "id": "k4-p-21",
   "c": 4,
   "p": "§ 21",
   "t": "Afgørelse om samvær",
   "s": "Ved uenighed om samværets omfang og udøvelse kan der træffes afgørelse, og der kan fastsættes de nødvendige vilkår. Loven nævner pasdeponering og vilkår om, at forælderen ikke må være påvirket af alkohol eller stoffer. Samværet fastsættes efter en konkret vurdering af barnets forhold. Samvær kan afslås, og en aftale eller afgørelse kan ændres eller ophæves.",
   "pr": "Der findes ingen standardordning i loven. Begrund ud fra netop dette barn: alder, tilknytning, afstand og konfliktniveau. Regler om overvåget samvær og erstatningssamvær står i bekendtgørelsen (§ 42).",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "Konkret vurdering af barnets forhold"
     ],
     [
      "i",
      "Vilkår: pasdeponering"
     ],
     [
      "i",
      "Vilkår: ikke påvirket af alkohol eller stoffer"
     ],
     [
      "n",
      "Samvær kan afslås, ændres eller ophæves"
     ]
    ]
   },
   "law": [
    [
     "§ 21",
     "Er der uenighed om omfanget og udøvelsen af samvær, kan der efter anmodning træffes afgørelse herom og fastsættes de nødvendige bestemmelser i forbindelse hermed, herunder vilkår om pasdeponering og vilkår til sikring af, at forælderen ikke er påvirket af alkohol eller euforiserende stoffer under samværet.|Stk. 2. Samværet fastsættes ud fra en konkret vurdering af barnets forhold.|Stk. 3. Fastsættelse af samvær kan afslås, og en aftale eller afgørelse om samvær kan ændres eller ophæves."
    ]
   ]
  },
  {
   "id": "k4-p-21-a",
   "c": 4,
   "p": "§ 21 a",
   "t": "Samvær stopper ikke af sig selv",
   "s": "Fastsat eller aftalt samvær bortfalder kun, hvis forældrene aftaler det, eller hvis Familieretshuset eller familieretten træffer afgørelse om det efter § 21 eller retsplejelovens § 456 q, stk. 4.",
   "pr": "En bopælsforælder kan ikke selv stoppe samværet, heller ikke hvis barnet ikke vil. Den rigtige vej er en anmodning om ændring eller ophævelse.",
   "v": {
    "k": "chk",
    "h": "Samvær bortfalder kun ved:",
    "i": [
     [
      "y",
      "Aftale mellem forældrene"
     ],
     [
      "y",
      "Afgørelse efter § 21"
     ],
     [
      "y",
      "Afgørelse efter retsplejelovens § 456 q, stk. 4"
     ],
     [
      "n",
      "En forælders ensidige beslutning"
     ]
    ]
   },
   "law": [
    [
     "§ 21 a",
     "Fastsat eller aftalt samvær bortfalder kun, når forældrene har aftalt det, eller når Familieretshuset eller familieretten har truffet afgørelse om bortfald af samvær efter § 21 eller efter § 456 q, stk. 4, i retsplejeloven."
    ]
   ]
  },
  {
   "id": "k4-p-22",
   "c": 4,
   "p": "§ 22",
   "t": "Anden kontakt",
   "s": "I særlige tilfælde kan der fastsættes anden kontakt, fx telefonsamtaler, breve, e-mail eller fotografier. Anmodningen kan komme fra samværsforælderen eller fra nærmeste pårørende, hvis betingelserne i § 20 er opfyldt. Der kan fastsættes vilkår, og kontakten kan afslås, ændres eller ophæves.",
   "pr": "Anden kontakt bruges typisk, når samvær ikke er muligt eller ikke er bedst for barnet, men kontakten bør bevares.",
   "v": {
    "k": "chk",
    "i": [
     [
      "i",
      "Telefon og video"
     ],
     [
      "i",
      "Breve og e-mail"
     ],
     [
      "i",
      "Fotografier"
     ],
     [
      "i",
      "Kun i særlige tilfælde"
     ]
    ]
   },
   "law": [
    [
     "§ 22",
     "I særlige tilfælde kan der træffes bestemmelse om anden kontakt med barnet i form af telefonsamtaler, brevveksling, elektronisk post, fotografier el. lign.|Stk. 2. Anmodning om anden kontakt kan indgives af|1) en forælder, som barnet ikke har bopæl hos, eller|2) barnets nærmeste pårørende, hvis betingelserne i § 20 for at få fastsat samvær er opfyldt.|Stk. 3. § 21, stk. 1 og 3, finder tilsvarende anvendelse."
    ]
   ]
  },
  {
   "id": "k4-p-23",
   "c": 4,
   "p": "§ 23",
   "t": "Ret til orientering om barnet",
   "s": "En forælder uden forældremyndighed har ret til orientering om barnet fra skoler, institutioner, social- og sundhedsvæsenet, private sygehuse, praktiserende læger og tandlæger. Fra skoler og institutioner kan forælderen også få dokumenter udleveret. Der må ikke gives fortrolige oplysninger om den, der har myndigheden. Institutionen kan nægte, hvis det skader barnet, og i særlige tilfælde kan retten fratages.",
   "pr": "Afslag efter stk. 2 kan påklages til Familieretshuset, dog ikke afslag fra sundhedsvæsenet (§ 41, stk. 2). Retten bortfalder ved midlertidig placering efter adoptionslovens § 32 a.",
   "v": {
    "k": "split",
    "a": [
     "Ret til orientering",
     [
      "Skoler og institutioner (også dokumenter)",
      "Social- og sundhedsvæsenet",
      "Privat læge, tandlæge og sygehus"
     ]
    ],
    "b": [
     "Grænser",
     [
      "Intet fortroligt om myndighedsindehaveren",
      "Kan nægtes ved skade for barnet",
      "Kan fratages i særlige tilfælde"
     ]
    ]
   },
   "law": [
    [
     "§ 23",
     "En forælder, som ikke har forældremyndigheden, har ret til efter anmodning at få orientering om barnets forhold fra skoler, børneinstitutioner, social- og sundhedsvæsenet samt private sygehuse, privat praktiserende læger og tandlæger. Denne forælder har desuden ret til at få udleveret dokumenter om barnets forhold, hvis disse findes på skoler og i børneinstitutioner. Der må ikke gives fortrolige oplysninger om forældremyndighedsindehaveren.|Stk. 2. De i stk. 1 nævnte institutioner m.v. kan nægte at give konkrete oplysninger og udlevere dokumenter om barnets forhold, hvis det må antages at være til skade for barnet.|Stk. 3. Der kan i særlige tilfælde efter anmodning fra indehaveren af forældremyndigheden eller fra en af de i stk. 1 nævnte institutioner m.v. træffes afgørelse om at fratage den forælder, der ikke har forældremyndigheden, adgangen til at få orientering og få udleveret dokumenter efter stk. 1. Afgørelsen har virkning fra det tidspunkt, hvor institutionen m.v. modtager meddelelse om afgørelsen.|Stk. 4. Adgangen til at få orientering og få udleveret dokumenter efter stk. 1 bortfalder, når barnet er midlertidigt placeret efter § 32 a i adoptionsloven. Dette har virkning fra det tidspunkt, hvor institutionen m.v. modtager meddelelse om, at barnet er midlertidigt placeret efter § 32 a i adoptionsloven."
    ]
   ]
  },
  {
   "id": "k4-p-24",
   "c": 4,
   "p": "§ 24",
   "t": "Anbragte børn",
   "nyt": "Henviser nu til barnets lov",
   "s": "Lovens regler om samvær og anden kontakt gælder ikke, når barnet er anbragt uden for hjemmet efter kapitel 5 i barnets lov. Det samme gælder, når barnet opholder sig på institution eller sygehus under en børnefaglig undersøgelse efter barnets lov § 20.",
   "pr": "Samvær med anbragte børn afgøres af kommunen efter barnets lov. Anmodninger skal i så fald henvises dertil.",
   "v": {
    "k": "split",
    "a": [
     "Familieretshuset",
     [
      "Samvær efter forældreansvarsloven"
     ]
    ],
    "b": [
     "Kommunen",
     [
      "Anbragt efter barnets lov kap. 5",
      "Børnefaglig undersøgelse efter § 20"
     ]
    ]
   },
   "law": [
    [
     "§ 24",
     "Bestemmelserne om samvær og anden kontakt i denne lov finder ikke anvendelse, hvis barnet er anbragt uden for hjemmet efter kapitel 5 i barnets lov. Tilsvarende gælder, hvis barnet eller den unge opholder sig på en institution eller er indlagt på sygehus under gennemførelse af en børnefaglig undersøgelse efter § 20 i barnets lov."
    ]
   ]
  },
  {
   "id": "k4-p-25",
   "c": 4,
   "p": "§ 25",
   "t": "Udlandsrejse trods uenighed",
   "s": "Er forældre med fælles myndighed uenige om forældremyndigheden, kan der træffes afgørelse om, at den ene må tage barnet med til udlandet, Grønland eller Færøerne i en kortere periode.",
   "pr": "Det er den anden undtagelse i § 3, stk. 2. Typisk handler det om en ferie. Overvej vilkår og en fast hjemrejsedato.",
   "v": {
    "k": "steps",
    "i": [
     "Uenighed om forældremyndigheden",
     "Samtykke til rejse mangler (§ 3, stk. 2)",
     "Afgørelse efter § 25",
     "Rejse i en kortere periode"
    ]
   },
   "law": [
    [
     "§ 25",
     "Selv om der er uenighed om forældremyndigheden mellem forældre, der har fælles forældremyndighed, kan der træffes afgørelse om, at den ene forælder kan tage barnet med til udlandet, Grønland eller Færøerne i en kortere periode."
    ]
   ]
  },
  {
   "id": "k4-quiz",
   "c": 4,
   "quiz": 1,
   "t": "Quiz · Kapitel 4",
   "qs": [
    {
     "id": "q4a",
     "q": "Barnet vil ikke på samvær, og mor stopper det. Hvad siger § 21 a?",
     "o": [
      "Det er i orden, hvis barnet er over 12",
      "Samværet bortfalder kun ved aftale eller afgørelse",
      "Mor skal blot give besked til Familieretshuset",
      "Samværet bortfalder efter 3 aflyste gange"
     ],
     "a": 1,
     "e": "Aftalt eller fastsat samvær bortfalder kun ved aftale mellem forældrene eller ved afgørelse."
    },
    {
     "id": "q4b",
     "q": "Barnet er anbragt efter barnets lov. Far søger samvær hos Familieretshuset. Hvad gælder?",
     "o": [
      "Familieretshuset afgør det efter § 21",
      "Samværsreglerne i loven gælder ikke, jf. § 24",
      "Familieretten afgør det",
      "Far skal vente til anbringelsen ophører"
     ],
     "a": 1,
     "e": "§ 24: lovens samværsregler gælder ikke for børn anbragt efter kapitel 5 i barnets lov."
    },
    {
     "id": "q4c",
     "q": "Hvad må skolen ikke oplyse en forælder uden forældremyndighed?",
     "o": [
      "Barnets fravær",
      "Karakterer",
      "Fortrolige oplysninger om forældremyndighedsindehaveren",
      "Referat fra skole-hjem-samtale"
     ],
     "a": 2,
     "e": "§ 23, stk. 1, 3. pkt."
    }
   ]
  },
  {
   "id": "k5-p-26",
   "c": 5,
   "p": "§ 26",
   "t": "Midlertidig myndighed og bopæl",
   "s": "Under en sag om forældremyndighed kan det efter anmodning bestemmes, hvem der midlertidigt har myndigheden, eller hos hvem barnet midlertidigt bor. Under en sag om bopæl kan der træffes midlertidig afgørelse om bopæl. Afgørelsen gælder, til der er en endelig aftale eller afgørelse, der kan fuldbyrdes. Den bortfalder, hvis anmodningen trækkes tilbage eller afvises, eller hvis forældrene flytter sammen igen.",
   "pr": "En midlertidig afgørelse skaber ofte en status quo, som vejer tungt i den endelige vurdering. Behandl den med samme omhu.",
   "v": {
    "k": "tl",
    "i": [
     [
      "Sagen starter",
      "anmodning om midlertidig afgørelse"
     ],
     [
      "Midlertidig afgørelse",
      "myndighed eller bopæl"
     ],
     [
      "Gælder indtil",
      "endelig aftale eller afgørelse, der kan fuldbyrdes"
     ],
     [
      "Bortfalder",
      "ved tilbagekaldelse, afvisning eller genoptaget samliv"
     ]
    ]
   },
   "law": [
    [
     "§ 26",
     "Under en sag om forældremyndighed kan den myndighed, der behandler sagen, efter anmodning bestemme, hvem forældremyndigheden midlertidigt skal tilkomme, eller hos hvem af forældrene barnet midlertidigt skal have bopæl. Under en sag om barnets bopæl kan det efter anmodning bestemmes, hos hvem af forældrene barnet midlertidigt skal have bopæl.|Stk. 2. En afgørelse efter stk. 1 gælder, indtil der foreligger en endelig aftale eller afgørelse, som kan fuldbyrdes.|Stk. 3. En afgørelse efter stk. 1 bortfalder, hvis anmodningen om, at der træffes afgørelse om forældremyndighed eller barnets bopæl, tilbagekaldes eller afvises, eller hvis forældrene genoptager samlivet."
    ]
   ]
  },
  {
   "id": "k5-p-27",
   "c": 5,
   "p": "§ 27",
   "t": "Risiko for bortførelse",
   "s": "Er der risiko for, at den ene forælder tager barnet ud af landet for at foregribe en afgørelse, kan socialministeren eller den, ministeren bemyndiger, midlertidigt give den anden forælder myndigheden alene. Afgørelsen bortfalder efter 4 uger, medmindre der inden da er søgt om afgørelse om forældremyndighed hos Familieretshuset.",
   "pr": "Afgørelser efter § 27 træffes ikke efter familieretshuslovens almindelige regler (§ 30 a, stk. 2, 2. pkt.). Anmodningen er undtaget fra kravet om digital selvbetjening (§ 31).",
   "v": {
    "k": "tl",
    "i": [
     [
      "Dag 0",
      "midlertidig myndighed alene"
     ],
     [
      "Inden 4 uger",
      "anmod om afgørelse hos Familieretshuset"
     ],
     [
      "Ellers",
      "afgørelsen bortfalder"
     ]
    ]
   },
   "law": [
    [
     "§ 27",
     "Har forældre fælles forældremyndighed, og er der risiko for, at den ene af dem vil bringe barnet ud af landet og derved foregribe en afgørelse om forældremyndigheden, kan socialministeren eller den, ministeren bemyndiger hertil, midlertidigt tillægge den anden af forældrene forældremyndigheden alene.|Stk. 2. En afgørelse efter stk. 1, hvorved en forælder får forældremyndigheden alene, gælder, indtil der foreligger en endelig aftale eller afgørelse om forældremyndigheden, som kan fuldbyrdes, jf. dog stk. 3.|Stk. 3. En afgørelse efter stk. 1, hvorved en forælder får forældremyndigheden alene, bortfalder|1) 4 uger efter afgørelsen, medmindre anmodning om afgørelse om forældremyndigheden inden da er indgivet til Familieretshuset, jf. § 31,|2) hvis en sag omfattet af nr. 1 efter indgivelsen tilbagekaldes eller afvises, eller|3) hvis forældrene genoptager samlivet."
    ]
   ]
  },
  {
   "id": "k5-p-28",
   "c": 5,
   "p": "§ 28",
   "t": "Når indehaveren er forhindret",
   "s": "Kan den eller dem, der har forældremyndigheden, ikke træffe beslutninger om barnet, afgør Familieretshuset, hvem der har myndigheden, så længe forhindringen varer.",
   "pr": "Eksempler er alvorlig sygdom, fængsling eller ophold i udlandet. Bestemmelsen bruges også til at udpege en midlertidig myndighedsindehaver for uledsagede mindreårige udlændinge.",
   "v": {
    "k": "steps",
    "i": [
     "Indehaveren er forhindret",
     "Familieretshuset afgør",
     "Gælder, så længe forhindringen varer"
    ]
   },
   "law": [
    [
     "§ 28",
     "Er indehaveren eller begge indehavere af forældremyndigheden forhindret i at træffe bestemmelse om barnets personlige forhold, afgør Familieretshuset, hvem forældremyndigheden skal tilkomme, så længe forhindringen varer."
    ]
   ]
  },
  {
   "id": "k5-p-29",
   "c": 5,
   "p": "§ 29",
   "t": "Midlertidigt samvær",
   "s": "Under en sag om forældremyndighed, bopæl, samvær eller anden kontakt kan der træffes afgørelse om midlertidigt samvær eller anden kontakt. Under en samværssag gælder den, til der er en endelig afgørelse eller aftale. Under en sag om myndighed eller bopæl bliver den midlertidige samværsafgørelse endelig, når sagen om myndighed eller bopæl er afgjort.",
   "pr": "Stk. 3 er let at overse: den midlertidige samværsafgørelse bliver endelig af sig selv. Vil en forælder have samværet ændret, kræver det en ny anmodning.",
   "v": {
    "k": "split",
    "a": [
     "Under en samværssag",
     [
      "Gælder til endelig afgørelse eller aftale om samvær eller anden kontakt"
     ]
    ],
    "b": [
     "Under myndigheds- eller bopælssag",
     [
      "Bliver endelig, når myndighed eller bopæl er afgjort"
     ]
    ]
   },
   "law": [
    [
     "§ 29",
     "Under en sag om forældremyndighed, om barnets bopæl, om samvær eller om anden kontakt kan der efter anmodning træffes afgørelse om midlertidigt samvær eller om anden kontakt.|Stk. 2. En afgørelse efter stk. 1 under en sag om samvær gælder, indtil der foreligger en endelig afgørelse om samvær, der kan fuldbyrdes, en aftale om samværet eller en afgørelse eller aftale om anden kontakt.|Stk. 3. En afgørelse efter stk. 1 under en sag om forældremyndighed eller barnets bopæl bliver endelig, når der foreligger en endelig afgørelse om forældremyndigheden eller barnets bopæl, der kan fuldbyrdes, eller en aftale om forældremyndigheden eller barnets bopæl."
    ]
   ]
  },
  {
   "id": "k5-p-29-a",
   "c": 5,
   "p": "§ 29 a",
   "t": "Kontaktbevarende samvær",
   "nyt": "Frister ændret 2025",
   "s": "Har barnet intet samvær med den forælder, der søger, skal Familieretshuset hurtigst muligt og inden 4 uger træffe afgørelse om kontaktbevarende samvær. Afslår Familieretshuset, skal der træffes afgørelse om midlertidigt samvær efter § 29 hurtigst muligt og senest 4 måneder efter anmodningen, medmindre forælderen ikke ønsker det.",
   "pr": "Tidligere var fristen 3 uger, og 4-månedersfristen fandtes ikke. Frister regnes fra modtagelsen af anmodningen. Hold øje med dem fra første dag.",
   "v": {
    "k": "tl",
    "i": [
     [
      "Dag 0",
      "anmodning om samvær modtages"
     ],
     [
      "Senest 4 uger",
      "afgørelse om kontaktbevarende samvær"
     ],
     [
      "Ved afslag: senest 4 mdr.",
      "afgørelse om midlertidigt samvær"
     ]
    ]
   },
   "law": [
    [
     "§ 29 a",
     "Har barnet ikke samvær med den forælder, der anmoder om samvær, skal Familieretshuset hurtigst muligt og inden 4 uger efter modtagelsen af en anmodning om fastsættelse af samvær træffe afgørelse om kontaktbevarende samvær. § 29, stk. 2 og 3, finder tilsvarende anvendelse.|Stk. 2. Afslår Familieretshuset at fastsætte kontaktbevarende samvær efter stk. 1, skal Familieretshuset træffe afgørelse om midlertidigt samvær efter § 29 hurtigst muligt og senest 4 måneder efter modtagelsen af anmodningen om fastsættelse af samvær. 1. pkt. gælder ikke, hvis anmodningen om fastsættelse af samvær ikke er omfattet af stk. 1 eller den forælder, der har anmodet om fastsættelse af samvær, meddeler Familieretshuset, at midlertidigt samvær ikke ønskes fastsat."
    ]
   ]
  },
  {
   "id": "k5-p-29-b",
   "c": 5,
   "p": "§ 29 b",
   "t": "Barn på krisecenter",
   "nyt": "Ny",
   "s": "Opholder barnet sig på et krisecenter efter servicelovens § 109 eller § 110 med den ene forælder, og godtgøres det, at opholdet skyldes vold fra den anden forælder, skal der efter anmodning træffes midlertidig afgørelse om, at barnet ikke har samvær eller anden kontakt med den anden forælder. Undtagelsen er, hvis kontakt anses for bedst for barnet.",
   "pr": "Her er udgangspunktet vendt om: ingen kontakt, medmindre kontakt er bedst for barnet. Vold skal være godtgjort. Det er ikke nok, at den er påstået.",
   "v": {
    "k": "ppl",
    "n": [
     [
      "m",
      "På krisecenter",
      90,
      70,
      "p"
     ],
     [
      "f",
      "Voldsudøver",
      310,
      70,
      "p"
     ],
     [
      "b",
      "Barn",
      200,
      190,
      "c"
     ]
    ],
    "l": [
     [
      "m",
      "b",
      "bo"
     ],
     [
      "f",
      "b",
      "x",
      "ingen kontakt"
     ]
    ]
   },
   "law": [
    [
     "§ 29 b",
     "Opholder et barn sig i en boform efter §§ 109 eller 110 i lov om social service med den ene forælder, og godtgøres det, at opholdet skyldes vold fra den anden forælder, skal der af hensyn til barnets bedste efter anmodning fra den forælder, som barnet opholder sig hos, træffes en midlertidig afgørelse om, at barnet ikke har samvær eller anden kontakt med den anden forælder. Dette gælder dog ikke, hvis det anses for bedst for barnet at have kontakt med den anden forælder."
    ]
   ]
  },
  {
   "id": "k5-p-30",
   "c": 5,
   "p": "§ 30",
   "t": "Midlertidige afgørelser kan ændres",
   "s": "En midlertidig afgørelse efter §§ 26 til 29 kan ændres.",
   "pr": "Nye oplysninger under sagen, fx om vold eller misbrug, kan begrunde en ændring uden at vente på den endelige afgørelse.",
   "v": {
    "k": "big",
    "n": "§§ 26–29",
    "u": "",
    "cap": "Alle midlertidige afgørelser kan ændres, mens sagen verserer."
   },
   "law": [
    [
     "§ 30",
     "En midlertidig afgørelse efter §§ 26-29 kan ændres."
    ]
   ]
  },
  {
   "id": "k5-quiz",
   "c": 5,
   "quiz": 1,
   "t": "Quiz · Kapitel 5",
   "qs": [
    {
     "id": "q5a",
     "q": "Hvad er fristen for afgørelse om kontaktbevarende samvær efter § 29 a?",
     "o": [
      "Inden 3 uger",
      "Inden 4 uger",
      "Inden 6 uger",
      "Inden 4 måneder"
     ],
     "a": 1,
     "e": "Fristen er 4 uger efter modtagelsen. 4 måneder er fristen for midlertidigt samvær, hvis kontaktbevarende samvær afslås."
    },
    {
     "id": "q5b",
     "q": "En afgørelse efter § 27 om midlertidig myndighed alene bortfalder, medmindre…",
     "o": [
      "der inden 4 uger søges om afgørelse om forældremyndighed",
      "politiet bekræfter risikoen",
      "den anden forælder accepterer",
      "familieretten stadfæster den"
     ],
     "a": 0,
     "e": "§ 27, stk. 3, nr. 1."
    },
    {
     "id": "q5c",
     "q": "Hvad kræver § 29 b for at træffe afgørelse om intet samvær?",
     "o": [
      "Påstand om vold",
      "Politianmeldelse",
      "At det godtgøres, at krisecenteropholdet skyldes vold fra den anden forælder",
      "Dom for vold"
     ],
     "a": 2,
     "e": "Volden skal være godtgjort, og barnet skal opholde sig på krisecenter efter §§ 109 eller 110."
    }
   ]
  },
  {
   "id": "k6-p-30-a",
   "c": 6,
   "p": "§ 30 a",
   "t": "Hvilke regler styrer sagsbehandlingen",
   "s": "Når Familieretshuset behandler sager efter loven, gælder lov om Familieretshuset. Afgørelser om aftaler og i sager om myndighed, bopæl, samvær, anden kontakt, orientering og udlandsrejse træffes af Familieretshuset eller familieretten efter kapitel 2 og 8 til 10 i den lov. Det gælder ikke afgørelser efter § 27.",
   "pr": "Forældreansvarsloven siger, hvad der skal afgøres. Familieretshusloven siger, hvordan sagen behandles, herunder visitation af sagerne.",
   "v": {
    "k": "split",
    "a": [
     "Forældreansvarsloven",
     [
      "Hvad der kan afgøres",
      "Kriterierne for afgørelsen"
     ]
    ],
    "b": [
     "Familieretshusloven",
     [
      "Hvordan sagen behandles",
      "Hvem der afgør (kap. 2 og 8–10)"
     ]
    ]
   },
   "law": [
    [
     "§ 30 a",
     "Ved Familieretshusets behandling af sager efter denne lov finder bestemmelserne i lov om Familieretshuset anvendelse.|Stk. 2. Afgørelse vedrørende aftaler om forældremyndighed og afgørelse i sager om forældremyndighed, bopæl, samvær, anden kontakt, orientering om barnet og udlandsrejse træffes efter bestemmelserne i kapitel 2 og 8-10 i lov om Familieretshuset af Familieretshuset eller familieretten. Dette gælder dog ikke afgørelser efter § 27."
    ]
   ]
  },
  {
   "id": "k6-p-31",
   "c": 6,
   "p": "§ 31",
   "t": "Digital selvbetjening",
   "nyt": "Omfatter nu også erklæringer",
   "s": "Anmeldelser, anmodninger om afgørelser eller erklæringer, anmodninger om indbringelse for familieretten og klager efter § 41, stk. 2 skal indgives digitalt. Undtaget er anmodninger fra offentlige myndigheder og om midlertidige afgørelser efter §§ 26, 27, 29 og 29 a. Andre henvendelser afvises, men Familieretshuset skal tilbyde en anden vej, hvis borgeren ikke kan bruge digital selvbetjening. Familieretshuset kan også undlade at afvise af økonomiske grunde eller af hensyn til barnet.",
   "pr": "Før du afviser en henvendelse på papir, skal du overveje stk. 2 og stk. 3. En digital henvendelse er modtaget, når den er tilgængelig for Familieretshuset.",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "Hovedregel: digital selvbetjening"
     ],
     [
      "i",
      "Undtaget: offentlige myndigheder og §§ 26, 27, 29, 29 a"
     ],
     [
      "i",
      "Særlige forhold: tilbyd en anden vej"
     ],
     [
      "n",
      "Ellers afvises henvendelsen"
     ]
    ]
   },
   "law": [
    [
     "§ 31",
     "Henvendelse om anmeldelse vedrørende en aftale, om anmodning om en afgørelse eller en erklæring, om anmodning om indbringelse af en afgørelse for familieretten og om klage efter § 41, stk. 2, skal indgives til Familieretshuset ved anvendelse af den digitale løsning, som Familieretshuset stiller til rådighed (digital selvbetjening). Dette gælder dog ikke anmodninger, der indgives af en offentlig myndighed, og anmodning om en midlertidig afgørelse efter §§ 26, 27, 29 og 29 a. Henvendelser, der ikke indgives ved digital selvbetjening, afvises af Familieretshuset, jf. dog stk. 2 og 3.|Stk. 2. Hvis Familieretshuset finder, at der foreligger særlige forhold, der gør, at borgeren ikke må forventes at kunne anvende digital selvbetjening, skal Familieretshuset tilbyde, at henvendelsen kan indgives på anden måde end ved digital selvbetjening efter stk. 1. Familieretshuset bestemmer, hvordan en henvendelse omfattet af 1. pkt. skal indgives, herunder om den skal indgives mundtligt eller skriftligt.|Stk. 3. Familieretshuset kan ud over i de i stk. 2 nævnte tilfælde undlade at afvise en henvendelse, der ikke er indgivet ved digital selvbetjening, hvis der ud fra en samlet økonomisk vurdering er klare fordele for Familieretshuset ved at modtage henvendelsen på anden måde end digitalt, eller hvis det må antages at være bedst for barnet, at henvendelsen ikke afvises.|Stk. 4. En digital henvendelse anses for at være kommet frem, når den er tilgængelig for Familieretshuset."
    ]
   ]
  },
  {
   "id": "k6-p-31-a",
   "c": 6,
   "p": "§ 31 a",
   "t": "Møde og mødepligt",
   "s": "Ved anmodning om afgørelse om myndighed (§§ 11, 14, 15, 15 a), bopæl (§ 17) eller samvær m.v. (§§ 19–21 og 25) indkalder Familieretshuset parterne til møde, medmindre det er unødvendigt eller uhensigtsmæssigt. Parterne har pligt til at møde, medmindre helt særlige omstændigheder gør sig gældende. En part skal ikke møde sammen med den anden, hvis den anden har været voldelig mod parten eller barnet, eller der er mistanke om det.",
   "pr": "Stk. 3 kræver kun mistanke om vold. Tilbyd separate møder, så snart vold nævnes.",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "Indkaldelse til møde"
     ],
     [
      "y",
      "Mødepligt for parterne"
     ],
     [
      "i",
      "Kan undlades, hvis unødvendigt eller uhensigtsmæssigt"
     ],
     [
      "n",
      "Ikke fælles møde ved vold eller mistanke om vold"
     ]
    ]
   },
   "law": [
    [
     "§ 31 a",
     "Ved anmodning om en afgørelse om forældremyndighed efter §§ 11, 14, 15 og 15 a, om barnets bopæl efter § 17 og om samvær m.v. efter §§ 19-21 og 25 indkalder Familieretshuset parterne til et møde. Familieretshuset kan undlade at indkalde parterne til et møde, hvis det er unødvendigt eller uhensigtsmæssigt at afholde et møde.|Stk. 2. Parterne har pligt til at deltage i mødet, medmindre helt særlige omstændigheder gør sig gældende.|Stk. 3. En part har ikke pligt til at deltage i mødet sammen med den anden part, hvis den anden part har udsat parten eller partens barn for voldelig adfærd, eller der er mistanke herom."
    ]
   ]
  },
  {
   "id": "k6-pp-32-32-a",
   "c": 6,
   "p": "§§ 32–32 a",
   "t": "Rådgivning og konfliktmægling",
   "s": "Ved uenighed om myndighed, bopæl eller samvær skal Familieretshuset tilbyde forældre og børn børnesagkyndig rådgivning eller konfliktmægling, medmindre det er unødvendigt eller uhensigtsmæssigt. I andre tilfælde kan det tilbydes ved særligt behov. Udsætter familieretten fuldbyrdelse efter retsplejelovens § 456 r, stk. 4, tilbyder Familieretshuset rådgivning, som skal være gennemført inden den dato, retten har udsat sagen til.",
   "pr": "Tilbuddet er en pligt, ikke et valg. Hvis du undlader at tilbyde det, skal begrundelsen fremgå af sagen.",
   "v": {
    "k": "steps",
    "i": [
     "Uenighed om myndighed, bopæl eller samvær",
     "Familieretshuset skal tilbyde rådgivning eller mægling",
     "Undtagelse: unødvendigt eller uhensigtsmæssigt",
     "§ 32 a: rådgivning ved udsat fuldbyrdelse"
    ]
   },
   "law": [
    [
     "§ 32",
     "Familieretshuset skal tilbyde forældre og børn børnesagkyndig rådgivning eller konfliktmægling ved uenighed om forældremyndighed, barnets bopæl eller samvær.|Stk. 2. Familieretshuset kan i andre tilfælde tilbyde børnesagkyndig rådgivning eller konfliktmægling, hvis der er et særligt behov herfor.|Stk. 3. Familieretshuset kan undlade at tilbyde rådgivning eller konfliktmægling efter stk. 1, hvis det er unødvendigt eller uhensigtsmæssigt."
    ],
    [
     "§ 32 a",
     "Familieretshuset tilbyder forældrene børnesagkyndig rådgivning, når familieretten efter § 456 r, stk. 4, i retsplejeloven har udsat fuldbyrdelse af forældremyndighed, barnets bopæl eller samvær. Rådgivning efter 1. pkt. skal være gennemført inden det tidspunkt, familieretten har udsat sagen til."
    ]
   ]
  },
  {
   "id": "k6-p-33",
   "c": 6,
   "p": "§ 33",
   "t": "Børnesagkyndige undersøgelser",
   "s": "Familieretshuset kan iværksætte børnesagkyndige undersøgelser og indhente sagkyndige erklæringer om forældrene i sager om myndighed, bopæl og samvær. En undersøgelse, der er sat i gang, gøres færdig, selv om sagen indbringes for familieretten.",
   "pr": "Se vejledningen om børnesagkyndige undersøgelser (VEJ nr. 9572 af 10. juni 2026) for krav til bestilling og indhold.",
   "v": {
    "k": "steps",
    "i": [
     "Behov for faglig vurdering",
     "Børnesagkyndig undersøgelse eller erklæring om forælder",
     "Færdiggøres, også hvis sagen går til familieretten"
    ]
   },
   "law": [
    [
     "§ 33",
     "Familieretshuset kan iværksætte børnesagkyndige undersøgelser og indhente sagkyndige erklæringer om forældre i sager om forældremyndighed, barnets bopæl og samvær.|Stk. 2. Har Familieretshuset iværksat en børnesagkyndig undersøgelse eller anmodet om en sagkyndig erklæring om en forælder, færdiggøres undersøgelsen eller erklæringen uanset sagens indbringelse for familieretten."
    ]
   ]
  },
  {
   "id": "k6-p-34",
   "c": 6,
   "p": "§ 34",
   "t": "Barnet skal inddrages",
   "s": "Barnet skal inddrages i sager om myndighed, bopæl og samvær, så dets perspektiv og synspunkter kommer frem. Det kan ske ved samtaler, børnesagkyndige undersøgelser eller på anden måde. Pligten til direkte inddragelse gælder ikke, hvis det kan skade barnet eller er unødvendigt.",
   "pr": "Loven har ingen aldersgrænse. Også små børns perspektiv skal belyses, fx gennem observation. Skriv, hvordan barnet er inddraget, eller hvorfor det ikke er sket.",
   "v": {
    "k": "word",
    "w": "Barnets perspektiv",
    "i": [
     "samtale",
     "undersøgelse",
     "anden måde",
     "ikke ved skade"
    ]
   },
   "law": [
    [
     "§ 34",
     "Barnet skal inddrages under en sag om forældremyndighed, barnets bopæl eller samvær, så dets perspektiv og eventuelle synspunkter kan komme til udtryk. Dette kan ske ved samtaler med barnet, børnesagkyndige undersøgelser eller på anden måde, der belyser barnets perspektiv.|Stk. 2. Forpligtelsen til at inddrage barnet direkte i sagen gælder ikke, hvis det må antages at være til skade for barnet, eller hvis det må anses for unødvendigt efter sagens omstændigheder."
    ]
   ]
  },
  {
   "id": "k6-p-35",
   "c": 6,
   "p": "§ 35",
   "t": "Barnets egen henvendelse",
   "s": "Et barn, der er fyldt 10 år, kan selv bede Familieretshuset indkalde forældrene til et møde om forældremyndighed, bopæl eller samvær.",
   "pr": "Barnet får ret til at få forældrene indkaldt. Barnet bliver ikke part i sagen og kan ikke selv anmode om en afgørelse.",
   "v": {
    "k": "big",
    "n": "10",
    "u": "år",
    "cap": "Fra 10 år kan barnet selv bede om et møde med forældrene."
   },
   "law": [
    [
     "§ 35",
     "Et barn, der er fyldt 10 år, kan anmode Familieretshuset om at indkalde forældrene til et møde om forældremyndigheden, barnets bopæl eller samvær."
    ]
   ]
  },
  {
   "id": "k6-pp-36-37",
   "c": 6,
   "p": "§§ 36–37",
   "t": "Erklæring fra forælderen",
   "s": "Før en aftale om overførsel til andre efter § 13, stk. 2 godkendes, eller før afgørelse efter § 15, stk. 3 eller § 15 a, stk. 2, skal der indhentes en erklæring fra den forælder, der ikke har del i myndigheden. Før afgørelse om pårørendesamvær efter § 20, stk. 2 skal den forælder, der ikke har samvær, afgive erklæring. Det kan undlades, hvis det skader barnet eller forsinker sagen uforholdsmæssigt. § 37 er ophævet.",
   "pr": "Erklæringen er en høring af forælderen, ikke et samtykke. Forælderen kan ikke blokere afgørelsen.",
   "v": {
    "k": "chk",
    "h": "Indhent erklæring før:",
    "i": [
     [
      "i",
      "Godkendelse efter § 13, stk. 2"
     ],
     [
      "i",
      "Afgørelse efter § 15, stk. 3 og § 15 a, stk. 2"
     ],
     [
      "i",
      "Pårørendesamvær efter § 20, stk. 2"
     ],
     [
      "n",
      "Kan undlades ved skade eller uforholdsmæssig forsinkelse"
     ]
    ]
   },
   "law": [
    [
     "§ 36",
     "Inden en aftale om forældremyndighed efter § 13, stk. 2, godkendes, eller der træffes afgørelse om forældremyndighed efter § 15, stk. 3, eller § 15 a, stk. 2, skal der indhentes en erklæring fra den forælder, der ikke har del i forældremyndigheden.|Stk. 2. Inden der træffes en afgørelse om samvær efter § 20, stk. 2, skal der indhentes en erklæring fra den forælder, der ikke eller kun i yderst begrænset omfang har samvær.|Stk. 3. Indhentelse af erklæring efter stk. 1 eller 2 kan undlades, hvis det må antages at være til skade for barnet eller medføre uforholdsmæssig forsinkelse af sagen."
    ],
    [
     "§ 37",
     "(Ophævet)"
    ]
   ]
  },
  {
   "id": "k6-p-38",
   "c": 6,
   "p": "§ 38",
   "t": "Økonomiske oplysninger",
   "s": "I sager om forældrenes fælles ansvar for transport ved samvær og fordeling af udgifterne kan Familieretshuset få terminaladgang til nødvendige økonomiske oplysninger hos skattemyndighederne, herunder indkomstregisteret.",
   "pr": "Adgangen gælder kun transportsager og kun de nødvendige oplysninger. Brug den ikke til andre formål.",
   "v": {
    "k": "steps",
    "i": [
     "Uenighed om transportudgifter ved samvær",
     "Opslag i indkomstregisteret",
     "Fordeling efter bekendtgørelsen"
    ]
   },
   "law": [
    [
     "§ 38",
     "Familieretshuset kan til brug for behandlingen af sager om forældrenes fælles ansvar for transport af barnet i forbindelse med samvær, herunder forældrenes afholdelse af udgifter herved, efter denne lov få terminaladgang til de nødvendige økonomiske oplysninger om en part hos told- og skatteforvaltningen, herunder i indkomstregisteret."
    ]
   ]
  },
  {
   "id": "k6-pp-39-40",
   "c": 6,
   "p": "§§ 39–40",
   "t": "Afvisning af ændringsanmodninger",
   "s": "En anmodning om ændring af myndighed, bopæl, samvær eller anden kontakt kan afvises, hvis forholdene ikke har ændret sig væsentligt. Det gælder ikke anmodninger om ændring efter § 14, stk. 2. § 40 er ophævet.",
   "pr": "Bestemmelsen beskytter barnet mod gentagne sager. Sammenlign med forholdene på tidspunktet for den seneste aftale eller afgørelse, og beskriv, hvad der er nyt.",
   "v": {
    "k": "steps",
    "i": [
     "Ny anmodning om ændring",
     "Har forholdene ændret sig væsentligt?",
     "Nej: kan afvises",
     "Undtagelse: § 14, stk. 2"
    ]
   },
   "law": [
    [
     "§ 39",
     "En anmodning om ændring af forældremyndighed, barnets bopæl, samvær eller anden kontakt kan afvises, hvis forholdene ikke har ændret sig væsentligt. Dette gælder dog ikke anmodninger om ændring af forældremyndighed efter § 14, stk. 2."
    ],
    [
     "§ 40",
     "(Ophævet)"
    ]
   ]
  },
  {
   "id": "k6-pp-41-41-a",
   "c": 6,
   "p": "§§ 41–41 a",
   "t": "Domstolsprøvelse og klage",
   "s": "Familieretshusets afgørelser kan indbringes for familieretten efter kapitel 12 i lov om Familieretshuset. En forælder uden myndighed kan klage til Familieretshuset over en institutions afslag på orientering efter § 23, stk. 2, dog ikke over afslag fra sundhedsvæsenet. I sager omfattet af familieretshuslovens § 24 træffer familieretten afgørelse efter §§ 11, 14, 17 og 21, når Familieretshuset anmoder om det.",
   "pr": "Vejledning om indbringelse skal fremgå af afgørelsen. Tjek familieretshuslovens § 24, når du vurderer, om en sag skal sendes direkte til familieretten.",
   "v": {
    "k": "steps",
    "i": [
     "Afgørelse fra Familieretshuset",
     "Anmodning om indbringelse (digitalt, § 31)",
     "Familieretten prøver sagen"
    ]
   },
   "law": [
    [
     "§ 41",
     "Familieretshusets afgørelser efter denne lov kan indbringes for familieretten efter bestemmelserne i kapitel 12 i lov om Familieretshuset.|Stk. 2. Den forælder, der ikke har forældremyndigheden, kan påklage en afgørelse efter § 23, stk. 2, til Familieretshuset. Dette gælder dog ikke afgørelser truffet af sundhedsvæsenet."
    ],
    [
     "§ 41 a",
     "I sager omfattet af § 24 i lov om Familieretshuset træffer familieretten afgørelse efter §§ 11, 14, 17 og 21 efter anmodning fra Familieretshuset."
    ]
   ]
  },
  {
   "id": "k6-p-42",
   "c": 6,
   "p": "§ 42",
   "t": "Bemyndigelser",
   "s": "Socialministeren kan fastsætte regler om sagsbehandling, erklæringer til børne- og ungeydelse, transport ved samvær, erstatningssamvær, overvåget samvær, anmeldelse af aftaler, rådgivning, undersøgelser, konfliktmægling og klagebehandling.",
   "pr": "De fleste af reglerne står i forældreansvarsbekendtgørelsen (BEK nr. 1756 af 30. december 2024). Den skal du kende ved siden af loven.",
   "v": {
    "k": "chk",
    "i": [
     [
      "i",
      "Sagsbehandling og erklæringer (kap. 7 b)"
     ],
     [
      "i",
      "Transport, erstatningssamvær og overvåget samvær"
     ],
     [
      "i",
      "Anmeldelse af aftaler"
     ],
     [
      "i",
      "Rådgivning, undersøgelser, mægling og klager"
     ]
    ]
   },
   "law": [
    [
     "§ 42",
     "Socialministeren kan fastsætte regler|1) om behandling af sager om forældremyndighed, barnets bopæl og samvær m.v. og om behandling og afgørelse af sager om erklæringer efter kapitel 7 b,|2) om forældrenes fælles ansvar for transport af barnet i forbindelse med samvær, herunder forældrenes afholdelse af udgifter herved,|3) om erstatningssamvær,|4) om overvåget samvær,|5) om anmeldelse af aftaler om forældremyndighed,|6) om børnesagkyndig rådgivning, børnesagkyndige undersøgelser, sagkyndige erklæringer om forældre og konfliktmægling og|7) om klagers behandling."
    ]
   ]
  },
  {
   "id": "k6-quiz",
   "c": 6,
   "quiz": 1,
   "t": "Quiz · Kapitel 6",
   "qs": [
    {
     "id": "q6a",
     "q": "Fra hvilken alder kan et barn selv bede Familieretshuset indkalde forældrene til møde?",
     "o": [
      "7 år",
      "10 år",
      "12 år",
      "15 år"
     ],
     "a": 1,
     "e": "§ 35: barnet skal være fyldt 10 år."
    },
    {
     "id": "q6b",
     "q": "Mor oplyser, at hun er bange for far på grund af vold, men der er ingen dom. Skal hun deltage i et fælles møde?",
     "o": [
      "Ja, mødepligten gælder",
      "Nej, mistanke om voldelig adfærd er nok",
      "Kun hvis far er dømt",
      "Kun hvis barnet har været udsat"
     ],
     "a": 1,
     "e": "§ 31 a, stk. 3: mistanke om voldelig adfærd mod parten eller barnet er nok."
    },
    {
     "id": "q6c",
     "q": "Hvornår kan en anmodning om ændring af samvær afvises efter § 39?",
     "o": [
      "Hvis der er truffet afgørelse inden for 1 år",
      "Hvis forholdene ikke har ændret sig væsentligt",
      "Hvis den anden forælder protesterer",
      "Aldrig"
     ],
     "a": 1,
     "e": "§ 39: afvisning er mulig, når forholdene ikke har ændret sig væsentligt."
    }
   ]
  },
  {
   "id": "k7-pp-43-44",
   "c": 7,
   "p": "§§ 43–44",
   "t": "Unges arbejdsaftaler",
   "s": "Har en ung under forældremyndighed med tilladelse selv påtaget sig arbejde og kan forsørge sig selv, kan den unge fra 15 år selv opsige aftalen og tage lignende arbejde, medmindre myndighedsindehaveren bestemmer andet. Omvendt kan myndighedsindehaveren ophæve en arbejdsaftale, som barnet selv har indgået, hvis hensynet til barnets opdragelse eller velfærd kræver det. Det skal ske med passende varsel, og arbejdsgiveren kan få erstatning.",
   "pr": "Reglerne bruges sjældent i Familieretshusets praksis, men de viser, at forældremyndigheden også omfatter unges arbejdsforhold.",
   "v": {
    "k": "big",
    "n": "15",
    "u": "år",
    "cap": "Den unge kan selv opsige og tage lignende arbejde, medmindre forældrene bestemmer andet."
   },
   "law": [
    [
     "§ 43",
     "Har et barn eller en ung, der er under forældremyndighed, jf. § 1 a, med tilladelse af forældremyndighedens indehaver på egen hånd påtaget sig tjeneste eller andet personligt arbejde, hvorved denne er blevet i stand til selv at sørge for sit underhold, kan den pågældende, hvis denne er fyldt 15 år, selv ophæve aftalen og påtage sig arbejde af lignende art, medmindre andet bestemmes af forældremyndighedens indehaver."
    ],
    [
     "§ 44",
     "Aftale om tjeneste eller andet personligt arbejde, som et barn eller en ung på egen hånd har truffet, kan forældremyndighedens indehaver ophæve, hvis hensynet til barnets eller den unges opdragelse eller velfærd kræver det. Ophævelsen skal dog så vidt muligt ske med passende varsel, og hvor det findes rimeligt, kan der tillægges den anden part en passende erstatning."
    ]
   ]
  },
  {
   "id": "k7-p-44-a",
   "c": 7,
   "p": "§ 44 a",
   "t": "Børn må ikke bruges som tolke",
   "nyt": "Ny 1. juli 2025",
   "s": "Børn under 18 år må ikke bruges som tolke ved mundtlig kommunikation med offentlige myndigheder eller med private leverandører, der løser opgaver for det offentlige. Det er kun tilladt, hvis kommunikationen handler om et uproblematisk forhold, eller hvis det er nødvendigt i et akut tilfælde.",
   "pr": "Det gælder også i Familieretshuset. Bestil en professionel tolk til møder og samtaler. Et barn må aldrig tolke i sin egen forældresag.",
   "v": {
    "k": "chk",
    "i": [
     [
      "n",
      "Barn under 18 som tolk hos myndigheder"
     ],
     [
      "y",
      "Undtagelse: uproblematisk forhold"
     ],
     [
      "y",
      "Undtagelse: akut og nødvendigt"
     ]
    ]
   },
   "law": [
    [
     "§ 44 a",
     "Børn under 18 år må ikke anvendes som tolke ved mundtlig kommunikation med offentlige forvaltningsmyndigheder og med private leverandører, der udfører opgaver for det offentlige, jf. dog stk. 2.|Stk. 2. Børn under 18 år må anvendes som tolke uanset stk. 1, når kommunikationen vedrører et uproblematisk forhold eller det er nødvendigt i et akut tilfælde."
    ]
   ]
  },
  {
   "id": "k7-p-44-b",
   "c": 7,
   "p": "§ 44 b",
   "t": "Erklæring til børne- og ungeydelse",
   "nyt": "Ny 1. januar 2026",
   "s": "Til brug for udbetaling af børne- og ungeydelse udsteder Familieretshuset efter anmodning fra en myndighedsindehaver en erklæring om, at barnet opholder sig mindst 9 ud af 14 dage hos den ene, eller at det ikke længere gør. Det forudsætter, at forældrene er enige, eller at den anden ikke protesterer.",
   "pr": "Protesterer den anden forælder, kan erklæringen ikke udstedes efter § 44 b. Anmodningen skal indgives digitalt (§ 31).",
   "v": {
    "k": "big",
    "n": "9/14",
    "u": "dage",
    "cap": "Erklæring om, hvor barnet opholder sig, til brug for børne- og ungeydelsen."
   },
   "law": [
    [
     "§ 44 b",
     "Til brug for udbetaling af børne- og ungeydelse efter lov om en børne- og ungeydelse udsteder Familieretshuset efter anmodning fra en forældremyndighedsindehaver følgende erklæringer, når forældremyndighedsindehaverne er enige om, at barnet opholder sig eller ikke længere opholder sig mindst 9 ud af 14 dage hos den ene forældremyndighedsindehaver, eller når den anden forældremyndighedsindehaver ikke protesterer mod udstedelsen:|1) Erklæring om, at barnet opholder sig mindst 9 ud af 14 dage hos den ene forældremyndighedsindehaver.|2) Erklæring om, at barnet ikke længere opholder sig mindst 9 ud af 14 dage hos den ene forældremyndighedsindehaver."
    ]
   ]
  },
  {
   "id": "k7-quiz",
   "c": 7,
   "quiz": 1,
   "t": "Quiz · Kapitel 7",
   "qs": [
    {
     "id": "q7a",
     "q": "En 14-årig tilbyder at tolke for sin far til et møde i Familieretshuset om samvær. Hvad gælder?",
     "o": [
      "Det er tilladt, hvis far ønsker det",
      "Det er ikke tilladt efter § 44 a",
      "Det er tilladt fra 12 år",
      "Det er tilladt, hvis mor accepterer"
     ],
     "a": 1,
     "e": "Et møde om samvær er ikke et uproblematisk forhold, så barnet må ikke bruges som tolk."
    },
    {
     "id": "q7b",
     "q": "Hvornår kan Familieretshuset udstede en erklæring efter § 44 b?",
     "o": [
      "Altid efter anmodning",
      "Når forældrene er enige, eller den anden ikke protesterer",
      "Kun efter en afgørelse om bopæl",
      "Kun ved delt bopæl"
     ],
     "a": 1,
     "e": "Kravet er enighed eller manglende protest fra den anden myndighedsindehaver."
    }
   ]
  },
  {
   "id": "k8-p-45",
   "c": 8,
   "p": "§ 45",
   "t": "Internationale aftaler",
   "s": "Regeringen kan indgå aftaler med andre lande om forholdet mellem dansk og udenlandsk ret om myndighed, bopæl, samvær og anden kontakt. Socialministeren kan fastsætte regler om forholdet til de nordiske lande og om sager, der er omfattet af Haagerbørnebeskyttelseskonventionen af 1996.",
   "pr": "Ved udenlandsk tilknytning skal du også kende børnebortførelsesloven og vejledningerne om international kompetence og anerkendelse af udenlandsk forældremyndighed.",
   "v": {
    "k": "chk",
    "i": [
     [
      "i",
      "Overenskomster med andre stater"
     ],
     [
      "i",
      "Nordiske regler"
     ],
     [
      "i",
      "Haagerbørnebeskyttelseskonventionen 1996"
     ]
    ]
   },
   "law": [
    [
     "§ 45",
     "Regeringen kan indgå overenskomst med andre stater om forholdet mellem dansk rets og fremmed rets regler om forældremyndighed, bopæl, samvær og anden kontakt. Overenskomsten finder anvendelse her i landet efter bekendtgørelse i Lovtidende.|Stk. 2. Socialministeren kan endvidere fastsætte regler om forholdet mellem danske og andre nordiske landes regler om forældremyndighed, barnets bopæl, samvær og anden kontakt.|Stk. 3. Socialministeren kan fastsætte regler om behandling af sager efter denne lov, der er omfattet af Haagerkonventionen af 19. oktober 1996 om kompetence, lovvalg, anerkendelse, fuldbyrdelse og samarbejde vedrørende forældreansvar og foranstaltninger til beskyttelse af børn (Haagerbørnebeskyttelseskonventionen)."
    ]
   ]
  },
  {
   "id": "k8-p-46",
   "c": 8,
   "p": "§ 46",
   "t": "Dansk kompetence",
   "s": "Familieretshuset kan kun behandle en sag om myndighed, bopæl eller samvær, hvis betingelserne i retsplejelovens § 448 g er opfyldt.",
   "pr": "Kompetencen er det første, du tjekker i en sag med udenlandsk tilknytning. Den samme regel afgør, om en omsorgs- og ansvarserklæring virker (§ 7, stk. 2).",
   "v": {
    "k": "steps",
    "i": [
     "Sag med udenlandsk tilknytning",
     "Er retsplejelovens § 448 g opfyldt?",
     "Ja: Familieretshuset kan behandle sagen",
     "Nej: ingen dansk kompetence"
    ]
   },
   "law": [
    [
     "§ 46",
     "Familieretshuset kan behandle en sag om forældremyndighed, barnets bopæl, samvær m.v., hvis betingelserne i § 448 g i retsplejeloven er opfyldt."
    ]
   ]
  },
  {
   "id": "k8-pp-47-49",
   "c": 8,
   "p": "§§ 47–49",
   "t": "Ikrafttræden og rigsfællesskabet",
   "nyt": "§ 49, stk. 2 er ny",
   "s": "Loven trådte i kraft 1. oktober 2007 og afløste lov om forældremyndighed og samvær. Reglerne i § 6, stk. 2, nr. 1, § 6, stk. 3 og § 7, stk. 3 gælder kun børn født 1. oktober 2007 eller senere. Loven gælder ikke for Færøerne og Grønland, men kan sættes i kraft der ved kongelig anordning. Det gælder også de dele af §§ 6 og 7, der endnu ikke gælder i Grønland.",
   "pr": "For ældre børn skal du kontrollere fødselsdatoen, før du anvender 10-månedersreglerne.",
   "v": {
    "k": "tl",
    "i": [
     [
      "1. okt. 2007",
      "loven træder i kraft"
     ],
     [
      "1. april 2019",
      "Familieretshuset overtager fra Statsforvaltningen"
     ],
     [
      "1. juli 2026",
      "gældende lovbekendtgørelse nr. 662"
     ]
    ]
   },
   "law": [
    [
     "§ 47",
     "Loven træder i kraft den 1. oktober 2007.|Stk. 2. Lov om forældremyndighed og samvær, jf. lovbekendtgørelse nr. 39 af 15. januar 2007, ophæves."
    ],
    [
     "§ 48",
     "Lovens § 6, stk. 2, nr. 1, § 6, stk. 3, og § 7, stk. 3, gælder kun for børn, der fødes den 1. oktober 2007 eller senere."
    ],
    [
     "§ 49",
     "Loven gælder ikke for Færøerne og Grønland, men kan ved kongelig anordning helt eller delvis sættes i kraft for Færøerne og Grønland med de afvigelser, som de særlige færøske og grønlandske forhold tilsiger.|Stk. 2. De dele af §§ 6 og 7, som ikke i medfør af stk. 1 er sat i kraft for Grønland, kan ved kongelig anordning sættes helt eller delvis i kraft for Grønland med de ændringer, som de grønlandske forhold tilsiger."
    ]
   ]
  },
  {
   "id": "k8-quiz",
   "c": 8,
   "quiz": 1,
   "t": "Quiz · Kapitel 8–9",
   "qs": [
    {
     "id": "q8a",
     "q": "Hvilken bestemmelse afgør, om Familieretshuset har kompetence i en sag med udenlandsk tilknytning?",
     "o": [
      "Forældreansvarslovens § 45",
      "Retsplejelovens § 448 g",
      "Haagerkonventionen alene",
      "Familieretshuslovens § 24"
     ],
     "a": 1,
     "e": "§ 46 henviser til retsplejelovens § 448 g."
    }
   ]
  },
  {
   "id": "k9-overblik",
   "c": 9,
   "p": "Overblik",
   "t": "Det skal du huske fra ændringerne",
   "s": "Hvis du kun husker ét fra de nye regler, så husk fristerne og beskyttelsen. Samarbejdschikane og fremmedgørelse skal indgå i vurderingen af barnets bedste. Kontaktbevarende samvær skal afgøres inden 4 uger. Børn på krisecenter efter vold har som udgangspunkt ingen kontakt med voldsudøveren. Formodningsreglen i § 4 a er udvidet, og børn må ikke bruges som tolke.",
   "pr": "Tag sluttesten. Den blander spørgsmål fra hele loven.",
   "v": {
    "k": "chk",
    "i": [
     [
      "y",
      "§ 4, stk. 2: chikane og fremmedgørelse"
     ],
     [
      "y",
      "§ 4 a: terror og § 244, stk. 2"
     ],
     [
      "y",
      "§ 29 a: 4 uger og 4 måneder"
     ],
     [
      "y",
      "§ 29 b: krisecenter og vold"
     ],
     [
      "y",
      "§ 44 a og § 44 b: tolke og børneydelse"
     ]
    ]
   }
  },
  {
   "id": "k9-quiz",
   "c": 9,
   "quiz": 1,
   "t": "Sluttest",
   "qs": [
    {
     "id": "qf1",
     "q": "Hvilken paragraf gør hensynet til barnets bedste til lovens grundprincip?",
     "o": [
      "§ 1",
      "§ 4 a",
      "§ 11",
      "§ 34"
     ],
     "a": 0,
     "e": "§ 1: hensynet til barnets bedste og ret til trivsel og beskyttelse kommer i første række."
    },
    {
     "id": "qf2",
     "q": "Far ønsker, at barnet flytter med til Spanien permanent. Mor siger nej. Forældrene har fælles myndighed og er uenige om den. Hvad kræves for udrejsen?",
     "o": [
      "Kun fars beslutning som bopælsforælder",
      "Mors samtykke eller en afgørelse efter § 17, stk. 1, 2. pkt.",
      "En afgørelse efter § 25",
      "Ingenting inden for EU"
     ],
     "a": 1,
     "e": "§ 3, stk. 2. En afgørelse efter § 25 dækker kun en kortere periode."
    },
    {
     "id": "qf3",
     "q": "Hvornår bortfalder en midlertidig bopælsafgørelse efter § 26?",
     "o": [
      "Efter 6 måneder",
      "Ved tilbagekaldelse, afvisning eller genoptaget samliv",
      "Når barnet fylder 12",
      "Efter 4 uger uden anmodning"
     ],
     "a": 1,
     "e": "§ 26, stk. 3. 4-ugersreglen hører til § 27."
    },
    {
     "id": "qf4",
     "q": "Bedstemor søger samvær. Barnets far er død. Hvilken bestemmelse?",
     "o": [
      "§ 19",
      "§ 20, stk. 1",
      "§ 20 a",
      "§ 22 alene"
     ],
     "a": 1,
     "e": "§ 20, stk. 1 gælder, når en forælder er død eller ukendt."
    },
    {
     "id": "qf5",
     "q": "Skal Familieretshuset tilbyde konfliktmægling ved uenighed om samvær?",
     "o": [
      "Nej, kun hvis forældrene beder om det",
      "Ja, medmindre det er unødvendigt eller uhensigtsmæssigt",
      "Kun familieretten kan",
      "Kun ved børn over 10"
     ],
     "a": 1,
     "e": "§ 32, stk. 1 og 3."
    },
    {
     "id": "qf6",
     "q": "Hvad gælder for erklæringen efter § 36?",
     "o": [
      "Forælderen skal give samtykke",
      "Den er en høring og kan undlades ved skade eller uforholdsmæssig forsinkelse",
      "Den kræves i alle sager",
      "Den er ophævet"
     ],
     "a": 1,
     "e": "§ 36, stk. 3."
    }
   ]
  },
  {
   "id": "k9-slut",
   "c": 9,
   "end": 1,
   "p": "Slut",
   "t": "Du er igennem hele loven"
  }
 ]
};

export default COURSE;
